/**
 * NewRegistrationPage - full-page registration wizard driven by
 * organisation-level registration settings (segment_order).
 *
 * The segment order is fetched from the API via useRegistrationSettings().
 * Each segment maps to a single independent step component:
 *   - customer          -> StepCustomer
 *   - host              -> StepHostProduct (host-only, product chosen elsewhere)
 *   - product           -> StepProductOnly
 *   - purchase          -> StepPurchase
 *   - review            -> StepReview
 *
 * Default segment_order = ['product', 'host', 'purchase', 'customer', 'review'].
 */

import {
  useState,
  useReducer,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { toast } from "sonner";
import { toastApiError } from "@/shared/lib/api-error";
import { unwrapStockAttributeValues } from "@/shared/lib/value-stock-field";
import api from "@/admin/lib/api";
import {
  ResourceLookupContextProvider,
  type ResourceLookupRegistry,
} from "@/shared/components/ui/resource-lookup-context";
import type { ResourceOption } from "@/shared/components/ui/resource-search-input";
import { useResourceLookupProviders } from "@/admin/hooks/use-resource-lookup-providers";
import { AppShell } from "@/admin/components/app-shell";
import { ConfirmationDialog } from "@/admin/components/shared/confirmation-dialog";
import {
  ConfigHelp,
  EditConfigLink,
  type ConfigSectionKey,
} from "@/admin/components/config-help";

import { RegistrationFieldsPanel } from "@/admin/components/registration-wizard/registration-fields-panel";
import { consumerPrefillFromMatch } from "@/admin/components/registration-wizard/duplicate-owner-prefill";
import {
  reconcilePinnedHost,
  resolveSubmittableExistingHostUuid,
} from "@/admin/components/registration-wizard/stale-existing-host";
import { StepHostSelection } from "@/admin/components/registration-wizard/step-host-selection";
import { StepHostProduct } from "@/admin/components/registration-wizard/step-host-product";
import { StepHostContext } from "@/admin/components/registration-wizard/step-host-context";
import { StepProductOnly } from "@/admin/components/registration-wizard/step-product-only";
import { StepProductType } from "@/admin/components/registration-wizard/step-product-type";
import { StepCustomer } from "@/admin/components/registration-wizard/step-customer";
import { StepPurchase } from "@/admin/components/registration-wizard/step-purchase";
import { StepPurchaseCustomer } from "@/admin/components/registration-wizard/step-purchase-customer";
import {
  DuplicateAddressDecisionModal,
  StepReview,
} from "@/admin/components/registration-wizard/step-review";
import { formatPropertyLineForDuplicateModal } from "@/admin/components/registration-wizard/format-property-display";
import { StepWarranty } from "@/admin/components/registration-wizard/step-warranty";
import { getDefaultWarrantyOptionUuid } from "@/admin/components/registration-wizard/shared";
import {
  RegistrationRuleAlerts,
  RegistrationStepActions,
} from "@/admin/components/registration-wizard/layout";
import {
  WizardValidationProvider,
  useRegisterValidator,
} from "@/admin/components/registration-wizard/wizard-validation";
import {
  useHoldingFormMetadata,
  useCreateHolding,
  useProductTemplates,
} from "@/admin/hooks/use-products";
import { useEntityTypes } from "@/admin/hooks/use-entities";
import { useFeatureFlag, FLAGS } from "@/admin/hooks/use-feature-flag";
import { useFormatting } from "@/admin/hooks/use-formatting";
import { useOrg } from "@/admin/hooks/use-org";
import {
  useWizardCacheKey,
  useWizardCachePersistence,
  readWizardCache,
  clearWizardCache,
} from "@/admin/hooks/use-wizard-cache";
import {
  useRegistrationSettings,
  SEGMENT_LABELS,
} from "@/admin/hooks/use-registration-settings";
import type { DataCaptureConfig } from "@/admin/hooks/use-registration-settings";
import {
  evaluateValidationRules,
  useValidationRules,
  type EvaluateResult,
  type RuleHit,
  type ValidationRule,
} from "@/admin/hooks/use-validation-rules";
import {
  useApprovalRules,
  type ApprovalRule,
  type ApprovalRuleHit,
} from "@/admin/hooks/use-approval-rules";
import {
  useRegistrationFields,
  isDisclaimerRegistrationField,
  type RegistrationField,
} from "@/admin/hooks/use-registration-fields";
import { usePartnershipDetail } from "@/admin/hooks/use-partnerships";
import type { PredicateNode } from "@/admin/components/condition-builder/types";
import {
  evaluatePredicateLocally,
  evaluateValidationRulesLocally,
} from "@/admin/lib/rules/local-validation";
import type { Product, RegistrationResponse } from "@/admin/types/products";
import type { ResolvedAddress } from "@/admin/hooks/use-lookups";
import { Button } from "@/shared/components/ui/button";
import {
  addressDisplay,
  buildAddress,
  isAddressNonEmpty,
  normaliseAddressValue,
} from "@/shared/lib/formatting";
import {
  isFieldVisible,
  isConditionallyRequired,
} from "@/shared/lib/forms/normalise";

// ---------------------------------------------------------------------------
// Step misconfiguration diagnostics
// ---------------------------------------------------------------------------

/**
 * A single configuration issue detected for the active step.
 *
 * The diagnostic is purely structural: it inspects what the metadata
 * declares the step must capture and reports anything the step has no
 * registered handler for. There are no field names baked into the
 * detector; everything is driven by template / registration-field
 * metadata. Surfaced as an inline error and blocks Continue when the
 * issue is required.
 */
export interface StepConfigIssue {
  field_code_name: string;
  display_name: string;
  reason: string;
  required: boolean;
}

/**
 * Segments that are host-capture surfaces. The wizard renders the host
 * panel on these segments; misconfiguration of host attributes is only
 * surfaced on these steps.
 */
const HOST_STEP_SEGMENTS = new Set<string>([
  "host",
  "host_context",
  "host_product",
]);

const HOST_LOOKUP_PROVIDERS_WITH_UI_HANDLER = new Set<string>([
  "",
  "address",
  "dvla",
]);

const SUPPORTED_FIELD_TYPES = new Set<string>([
  "TEXT",
  "RICH_TEXT",
  "LONG_TEXT",
  "TEXTAREA",
  "NUMBER",
  "DECIMAL",
  "BOOLEAN",
  "SELECT",
  "DROPDOWN",
  "MULTI_SELECT",
  "DATE",
  "DATETIME",
  "ADDRESS",
  "EMAIL",
  "URL",
  "PHONE",
  "ENTITY_SEARCH",
]);

interface StepMisconfigInputs {
  segment: string | undefined;
  segments: string[];
  hostTemplateAttributes: Array<{
    code_name: string;
    display_name: string;
    required: boolean;
    lookup_provider: string;
  }> | null;
  hostStepSegments: Set<string>;
  hostInstalled: boolean | null;
  hostRequirement: string;
  registrationFields: Array<{
    code_name: string;
    display_name: string;
    required: boolean;
    is_active: boolean;
    field_type: string;
    capture_step: string | null;
  }> | null;
  registrationFieldsEnabled: boolean;
}

function detectStepConfigIssues(
  inputs: StepMisconfigInputs,
): StepConfigIssue[] {
  const issues: StepConfigIssue[] = [];
  const {
    segment,
    segments,
    hostTemplateAttributes,
    hostStepSegments,
    hostInstalled,
    hostRequirement,
    registrationFields,
    registrationFieldsEnabled,
  } = inputs;

  if (!segment) return issues;

  // Host attributes are this step's responsibility iff the step is
  // declared as a host capture surface AND host data is expected.
  if (
    hostStepSegments.has(segment) &&
    hostTemplateAttributes &&
    !(hostRequirement === "OPTIONAL" && hostInstalled === false)
  ) {
    for (const attr of hostTemplateAttributes) {
      if (!attr.required) continue;
      const provider = attr.lookup_provider ?? "";
      if (!HOST_LOOKUP_PROVIDERS_WITH_UI_HANDLER.has(provider)) {
        issues.push({
          field_code_name: attr.code_name,
          display_name: attr.display_name,
          required: true,
          reason: `Lookup provider \`${provider}\` has no UI handler. Update the template or register a handler.`,
        });
      }
    }
  }

  // Registration fields whose capture step matches the active segment.
  if (registrationFieldsEnabled && registrationFields) {
    for (const field of registrationFields) {
      if (!field.is_active) continue;
      const captureStep = field.capture_step ?? null;

      // Anchor orphaned-capture-step warnings to the first real capture
      // segment, not the synthetic `product_type` chooser, so prepending it
      // doesn't change which step surfaces the config issue.
      const anchorSegment =
        segments.find((s) => s !== "product_type") ?? segments[0];
      if (
        captureStep &&
        !segments.includes(captureStep) &&
        segment === anchorSegment
      ) {
        issues.push({
          field_code_name: field.code_name,
          display_name: field.display_name,
          required: field.required,
          reason: `Capture step \`${captureStep}\` is not in this organisation's segment order.`,
        });
        continue;
      }

      if (captureStep !== segment) continue;

      const ft = (field.field_type ?? "").toUpperCase();
      if (!SUPPORTED_FIELD_TYPES.has(ft)) {
        issues.push({
          field_code_name: field.code_name,
          display_name: field.display_name,
          required: field.required,
          reason: `Field type \`${field.field_type}\` is not supported by the renderer.`,
        });
      }
    }
  }

  return issues;
}

/**
 * Page-level "hard block" validator. Registers with the wizard
 * validation context so clicking Continue does not advance when the
 * page surfaces a non-field blocker (rule BLOCK hits, server-side
 * validation in flight, misconfigured step). The block reasons render
 * separately as banners; this component only gates progression.
 */
function PageHardBlockValidator({ blocked }: { blocked: boolean }) {
  useRegisterValidator("__page_hard_block__", !blocked);
  return null;
}

function StepConfigurationAlert({ issues }: { issues: StepConfigIssue[] }) {
  if (issues.length === 0) return null;
  return (
    <div className="mb-5 rounded-lg border border-[var(--ink)] bg-[var(--bg-subtle)] px-4 py-3">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="m-0 text-[12px] font-light uppercase tracking-[0.08em] text-[var(--ink)]">
            Step misconfiguration
          </p>
          <p className="mt-1 text-[13px] text-[var(--ink-2)]">
            The current step declares fields that the form cannot render.
            Contact your administrator to update the template configuration.
          </p>
          <ul className="mt-3 space-y-1.5 pl-0">
            {issues.map((issue) => (
              <li
                key={issue.field_code_name}
                className="list-none text-[13px] text-[var(--ink-2)]"
              >
                <span className="font-light text-[var(--ink)]">
                  {issue.display_name}
                </span>
                <span className="ml-1 text-[var(--ink-3)]">
                  (<code className="font-mono">{issue.field_code_name}</code>
                  {issue.required ? ", required" : ""})
                </span>
                <span className="block text-[12px] text-[var(--ink-3)]">
                  {issue.reason}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step label generation - enriches segment labels with host kind context
// ---------------------------------------------------------------------------

function getSegmentLabel(segment: string): string {
  if (segment === "product") {
    return "Product";
  }
  if (segment === "host") {
    return "Host";
  }
  return SEGMENT_LABELS[segment] || segment;
}

/**
 * Resolve which product template drives the registration wizard.
 *
 * When a tenant has more than one registration-selectable template the
 * wizard inserts an explicit "product type" step and stores the choice in
 * `state.productTemplateUuid`. Prefer that explicit choice; otherwise fall
 * back to the first host-bearing template (legacy single-template
 * behaviour) so single-template tenants behave exactly as before.
 */
function resolveRegistrationTemplate<
  T extends { uuid: string; host_template: unknown | null },
>(templates: T[], productTemplateUuid: string | null): T | undefined {
  if (productTemplateUuid) {
    const chosen = templates.find((t) => t.uuid === productTemplateUuid);
    if (chosen) return chosen;
  }
  return templates.find((t) => t.host_template) ?? templates[0];
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export interface GeoEligibleProduct {
  uuid: string;
  category?: { uuid: string; name: string } | null;
  product_type: string;
}

export interface GeoEligibilityResult {
  zone: { uuid: string; name: string; code: string } | null;
  psa: { uuid: string; name: string; code: string } | null;
  method: string;
  confidence: string;
  productUuids: string[];
  products: GeoEligibleProduct[];
  productCount: number;
  // False when the scoped product template opts out of category-aware PSA
  // (geo) filtering - the product list is the template's catalogue, not a
  // geo-narrowed set, so PSA-specific banners/empty-state copy must not show.
  psaFilteringApplied: boolean;
}

export type DuplicateAddressDecision =
  "merge" | "reject" | "add_new_product" | "confirmed_duplicate";

export interface DuplicateAddressMatchSummary {
  ref: string;
  holding_uuid: string;
  host_uuid?: string | null;
  registration_number?: string | null;
  owner_name?: string | null;
  owner_first_name?: string | null;
  owner_last_name?: string | null;
  owner_email?: string | null;
  owner_phone?: string | null;
  product_name?: string | null;
  product_uuid?: string | null;
  consumer_account_uuid?: string | null;
  structure_values?: Record<string, unknown>;
  registration_status?: {
    state_key: string;
    state_label: string;
    state_color: string;
  } | null;
  /** WPS-150 registration-view enrichment: the newest non-deleted claim's
   *  reference on this holding, and the holding's installation date. Both are
   *  populated only on the batched address-match path and are null otherwise. */
  claim_reference?: string | null;
  installation_date?: string | null;
}

export interface DuplicateAddressResolutionContext {
  propertyAddress: string;
  ownerName: string;
  existingOwnerName: string;
  installedDate: string;
  matches: DuplicateAddressMatchSummary[];
  selectedMatch?: DuplicateAddressMatchSummary | null;
  /**
   * When the user continues a duplicate-address registration against the
   * matched property, this records WHICH path they took (WP1-34):
   *  - `new_structure`  → add a new structure to the matched property; the
   *    new structure's attributes ride along in `state.hostAttributeValues`
   *    and the backend creates/attaches it via `host_data`.
   *  - `existing_structure` → register the product against one of the
   *    property's existing structures (`selectedStructureUuid`).
   */
  structurePath?: "new_structure" | "existing_structure" | null;
  /** Property host the new holding attaches to (the matched property). */
  selectedHostUuid?: string | null;
  /** Existing structure chosen for the `existing_structure` path. */
  selectedStructureUuid?: string | null;
}

export interface RegistrationWizardIssue {
  id: string;
  kind: "duplicate_address";
  severity: RuleHit["severity"];
  message: string;
  addressKey: string;
  hits: RuleHit[];
  status: "open" | "resolved";
  decision?: DuplicateAddressDecision;
  decisionLabel?: string;
  notes?: string;
  /**
   * Holding UUID the user picked to merge into. Only set when
   * `decision === 'merge'`; narrows ``duplicate_holding_uuids`` on the
   * server payload down from "all matches" to the explicit choice.
   */
  selectedHoldingUuid?: string;
  resolutionContext?: DuplicateAddressResolutionContext;
  resolvedAt?: string;
}

export interface DuplicateAddressResolution {
  id: string;
  decision: Exclude<DuplicateAddressDecision, "confirmed_duplicate">;
  decisionLabel?: string;
  notes?: string;
  addressKey: string;
  hits: RuleHit[];
  selectedHoldingUuid?: string;
  resolutionContext?: DuplicateAddressResolutionContext;
  resolvedAt: string;
}

export interface WizardState {
  step: number;
  // Product type (which registration-selectable product template this
  // registration is for). Null until chosen; only surfaced as an explicit
  // step when the tenant has more than one selectable template.
  productTemplateUuid: string | null;
  // Product
  selectedProduct: Product | null;
  // True when Find a Product resolved a unique stocked value (serial) to
  // this product. The catalogue picker then shows product + model as
  // read-only - the operator searches again rather than editing them.
  productLockedByStock: boolean;
  // Holding attribute the lock was resolved from, so the serial can be
  // prefilled on the later details step.
  stockResolvedAttribute: string | null;
  // Dynamic attributes (generic catch-all for HOLDING attrs)
  dynamicAttributes: Record<string, unknown>;
  // Early registration attributes (collected before product selection)
  earlyAttributes: Record<string, unknown>;
  // Tenant-scoped registration field values (RegistrationField EAV)
  registrationFieldValues: Record<string, unknown>;
  // Rule issues surfaced during the wizard and resolved during Review.
  registrationIssues: RegistrationWizardIssue[];
  // Resolved duplicate-address decision retained for submission only.
  duplicateAddressResolution: DuplicateAddressResolution | null;
  // Geo eligibility (populated by host_context step)
  geoEligibility: GeoEligibilityResult | null;
  // Purchase
  purchaseDate: string;
  purchasePrice: string;
  priceCurrency: string;
  salesPerson: string;
  salesRefNumber: string;
  // Customer
  consumerEmail: string;
  // Whose the typed email is, when the registrant said so on the Customer
  // step: false = theirs, true = the customer's own, null = let the rule infer.
  consumerEmailIsOwners: boolean | null;
  consumerFirstName: string;
  consumerLastName: string;
  consumerPhone: string;
  // Tenant-authored customer fields (e.g. a landline), keyed by code_name.
  consumerExtraValues: Record<string, unknown>;
  address: ResolvedAddress | null;
  // Customer/consumer mailing address. It may be displayed on the Property
  // step so users can choose whether it mirrors the installation address,
  // but it is persisted on the consumer entity.
  mailingSameAsInstallation: boolean;
  // Registration consent ticks keyed by kind (T&C, marketing email).
  consents: Record<string, boolean>;
  // Partnership (dealer selection for tenant admins)
  partnershipUuid: string | null;
  partnershipName: string | null;
  // Warranty
  warrantyTemplateUuid: string | null;
  warrantyTemplateOptionUuid: string | null;
  warrantyStartDate: string;
  // Transaction template (data-driven purchase fields)
  transactionData: Record<string, unknown>;
  // Rich upload envelopes for transaction file/photo attributes, kept for
  // display only (the review card renders filename links). `transactionData`
  // keeps the bare UUID the submission payload requires.
  transactionUploads: Record<string, unknown>;
  // Host template (data-driven)
  hostAttributeValues: Record<string, unknown>;
  isInstalled: boolean | null;
  // Existing host selection
  selectedExistingHostUuid: string | null;
  // Address key (see addressDuplicateKey) the pinned existing host was chosen
  // for. Lets us drop the pin when the property address is changed afterwards
  // so the warranty never registers against the previous address (WPS-137).
  // `null` = pinned but not yet observed; captured on the next render.
  selectedExistingHostAddressKey: string | null;
  // Post-submission flag
  registrationComplete: boolean;
}

export type SubmitRegistrationResult =
  { type: "submitted"; response: RegistrationResponse } | { type: "aborted" };

type ServerStepValidation = {
  step: string;
  hits: RuleHit[];
};

type PendingAddressDuplicateWarning = {
  hits: RuleHit[];
  advanceAfterConfirm: boolean;
} | null;

type EffectiveRegistrationField = {
  field: RegistrationField;
  required: boolean;
  requiredMessage: string | null;
};

type PropertyRegistrationPrefill = {
  hostUuid?: string | null;
  hostAttributeValues?: Record<string, unknown>;
  hostColumnValues?: Record<string, unknown>;
  structureAttributeValues?: Record<string, unknown>;
  customerPrefill?: {
    email?: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
  };
  earlyAttributesPrefill?: Record<string, unknown>;
};

type NewRegistrationLocationState = {
  propertyRegistrationPrefill?: PropertyRegistrationPrefill;
  returnTo?: string;
  /**
   * When set (e.g. navigated from "New Registration" inside a BFS/SunSuite
   * claim form), the product step will only show products belonging to the
   * named product template. Empty string / absent means show all products.
   */
  productTemplateName?: string;
  /**
   * When navigated from the Submit Claim wizard, the claim schema key that
   * was selected (e.g. "bfs", "installed"). Threaded back into the returnTo
   * URL so the claim form reopens with the correct schema rather than
   * defaulting to "installed".
   */
  claimSchemaKey?: string;
};

function mergePropertyHostPrefill(
  prefill: PropertyRegistrationPrefill,
  hostTemplateAttributes: Array<{
    code_name: string;
    maps_to_column?: string | null;
  }> = [],
): Record<string, unknown> {
  const values: Record<string, unknown> = {
    ...(prefill.hostAttributeValues ?? {}),
  };
  const columnValues = prefill.hostColumnValues ?? {};
  const structureValues = prefill.structureAttributeValues ?? {};

  for (const attr of hostTemplateAttributes) {
    const candidates = [attr.code_name, attr.maps_to_column].filter(
      (candidate): candidate is string => Boolean(candidate),
    );

    for (const key of candidates) {
      const value = columnValues[key] ?? structureValues[key];
      if (
        value !== undefined &&
        value !== null &&
        value !== "" &&
        (values[attr.code_name] === undefined || values[attr.code_name] === "")
      ) {
        values[attr.code_name] = value;
      }
    }
  }

  return values;
}

function mergeRuleHits(...collections: RuleHit[][]): RuleHit[] {
  const merged = new Map<string, RuleHit>();
  for (const collection of collections) {
    for (const hit of collection) {
      merged.set(hit.rule_uuid, hit);
    }
  }
  return [...merged.values()];
}

const ADDRESS_DUPLICATE_REFS = new Set([
  "entity:host.address",
  "entity:host.address_line_1",
  "entity:host.address_postcode",
]);

function predicateContainsAddressDuplicateRule(
  predicate: PredicateNode | null | undefined,
): boolean {
  if (!predicate) return false;
  if (predicate.type === "clause") {
    return (
      predicate.op === "already_exists" &&
      ADDRESS_DUPLICATE_REFS.has(predicate.ref)
    );
  }
  return predicate.children.some(predicateContainsAddressDuplicateRule);
}

function isDuplicateAddressReviewSegment(
  segment: string | null | undefined,
): boolean {
  return (
    segment === "host_context" ||
    segment === "host" ||
    segment === "host_product" ||
    segment === "review"
  );
}

function addressDuplicateKey(ruleContext: {
  fields?: Record<string, unknown>;
  entities?: Record<string, Record<string, unknown>>;
}): string {
  const host = ruleContext.entities?.host ?? {};
  const addressPayload = Object.values(host).find(
    (value): value is Record<string, unknown> =>
      !!value && typeof value === "object" && !Array.isArray(value),
  );
  const line1 =
    host.address_line_1 ??
    addressPayload?.addressLine1 ??
    addressPayload?.line_1 ??
    "";
  const postcode =
    host.address_postcode ??
    addressPayload?.postalCode ??
    addressPayload?.postcode ??
    "";
  const city =
    host.address_town_or_city ??
    addressPayload?.locality ??
    addressPayload?.town_or_city ??
    addressPayload?.city ??
    "";
  const country =
    host.address_country ??
    addressPayload?.countryCode ??
    addressPayload?.country ??
    "";
  return [line1, postcode, city, country]
    .map((part) =>
      String(part ?? "")
        .trim()
        .toLowerCase(),
    )
    .join("|");
}

function filterAddressDuplicateHits(
  hits: RuleHit[],
  ruleByUuid: Map<string, ValidationRule>,
): RuleHit[] {
  return hits.filter((hit) =>
    isNonBlockingAddressDuplicateHit(hit, ruleByUuid),
  );
}

function syncDuplicateAddressIssueState(
  issues: RegistrationWizardIssue[],
  params: {
    segment: string | null | undefined;
    addressKey: string;
    hits: RuleHit[];
    ruleByUuid: Map<string, ValidationRule>;
    resolvedAddressKey?: string | null;
  },
): RegistrationWizardIssue[] {
  if (!isDuplicateAddressReviewSegment(params.segment)) return issues;
  if (
    params.resolvedAddressKey &&
    params.resolvedAddressKey === params.addressKey
  ) {
    return issues.filter((issue) => issue.kind !== "duplicate_address");
  }
  const duplicateHits = filterAddressDuplicateHits(
    params.hits,
    params.ruleByUuid,
  );
  if (duplicateHits.length > 0) {
    return upsertDuplicateAddressIssue(issues, {
      addressKey: params.addressKey,
      hits: duplicateHits,
    });
  }
  return issues.filter((issue) => issue.kind !== "duplicate_address");
}

function registrationIssuesEqual(
  left: RegistrationWizardIssue[],
  right: RegistrationWizardIssue[],
): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function isNonBlockingAddressDuplicateHit(
  hit: RuleHit,
  ruleByUuid: Map<string, ValidationRule>,
): boolean {
  if (hit.severity === "BLOCK") return false;
  const rule = ruleByUuid.get(hit.rule_uuid);
  return predicateContainsAddressDuplicateRule(rule?.conditions);
}

function upsertDuplicateAddressIssue(
  issues: RegistrationWizardIssue[],
  params: {
    addressKey: string;
    hits: RuleHit[];
  },
): RegistrationWizardIssue[] {
  const existing = issues.find(
    (issue) =>
      issue.kind === "duplicate_address" &&
      issue.addressKey === params.addressKey,
  );
  const firstHit = params.hits[0];
  const nextIssue: RegistrationWizardIssue = {
    id: existing?.id ?? `duplicate_address:${params.addressKey}`,
    kind: "duplicate_address",
    severity: firstHit?.severity ?? "WARN",
    message:
      firstHit?.message ?? "A registration already exists with this address.",
    addressKey: params.addressKey,
    hits: params.hits,
    status: existing?.status ?? "open",
    decision: existing?.decision,
    decisionLabel: existing?.decisionLabel,
    notes: existing?.notes,
    selectedHoldingUuid: existing?.selectedHoldingUuid,
    resolutionContext: existing?.resolutionContext,
    resolvedAt: existing?.resolvedAt,
  };

  return [...issues.filter((issue) => issue.id !== nextIssue.id), nextIssue];
}

/**
 * Merge tenant-defined RegistrationField values into the host attribute
 * payload. Registration fields win on key collision because they are the
 * canonical organisation-level source once configured.
 *
 * Generic by design: no hardcoded code_name lives in this helper. The
 * shape of what flows into ``entities.host`` (rule context) and
 * ``host_data`` (submit payload) is fully driven by the tenant's
 * RegistrationField definitions.
 */
function mirrorRegistrationFieldsToHost(
  hostAttributeValues: Record<string, unknown>,
  registrationFieldValues: Record<string, unknown>,
  options: { dropEmpty?: boolean } = {},
): Record<string, unknown> {
  const dropEmpty = options.dropEmpty === true;
  const out: Record<string, unknown> = {};
  const writable = (val: unknown) =>
    !(dropEmpty && (val === undefined || val === null || val === ""));

  for (const [k, v] of Object.entries(hostAttributeValues)) {
    if (writable(v)) out[k] = v;
  }
  for (const [k, v] of Object.entries(registrationFieldValues)) {
    if (writable(v)) out[k] = v;
  }
  return out;
}

function buildHostRuleEntity(
  hostAttributeValues: Record<string, unknown>,
  registrationFieldValues: Record<string, unknown>,
  hostTemplateAttributes: Array<{
    code_name: string;
    maps_to_column: string;
  }> = [],
): Record<string, unknown> {
  const hostEntity = mirrorRegistrationFieldsToHost(
    hostAttributeValues,
    registrationFieldValues,
  );

  for (const attribute of hostTemplateAttributes) {
    const value = hostAttributeValues[attribute.code_name];
    if (
      value !== undefined &&
      value !== null &&
      value !== "" &&
      attribute.maps_to_column &&
      hostEntity[attribute.maps_to_column] === undefined
    ) {
      hostEntity[attribute.maps_to_column] = value;
    }
  }

  return hostEntity;
}

function isRegistrationFieldValueProvided(
  field: { field_type: string },
  value: unknown,
): boolean {
  if (typeof value === "boolean") return true;
  if (typeof value === "number") return !Number.isNaN(value);
  if (Array.isArray(value)) return value.length > 0;
  if (field.field_type === "MULTI_SELECT" && typeof value === "string") {
    return value.trim().length > 0;
  }
  if (typeof value === "string") return value.trim().length > 0;
  return value !== null && value !== undefined;
}

const initialState: WizardState = {
  step: 0,
  productTemplateUuid: null,
  selectedProduct: null,
  productLockedByStock: false,
  stockResolvedAttribute: null,
  dynamicAttributes: {},
  earlyAttributes: {},
  registrationFieldValues: {},
  registrationIssues: [],
  duplicateAddressResolution: null,
  geoEligibility: null,
  transactionData: {},
  transactionUploads: {},
  // Blank by default so the CS user enters the real purchase date. It is
  // optional, and future dates are blocked by the date picker
  // (`shouldDisableFutureDates`). Pre-filling today silently recorded the
  // wrong purchase date whenever the field was left untouched.
  purchaseDate: "",
  purchasePrice: "",
  priceCurrency: "GBP",
  salesPerson: "",
  salesRefNumber: "",
  consumerEmail: "",
  consumerEmailIsOwners: null,
  consumerFirstName: "",
  consumerLastName: "",
  consumerPhone: "",
  consumerExtraValues: {},
  address: null,
  mailingSameAsInstallation: true,
  consents: {},
  partnershipUuid: null,
  partnershipName: null,
  warrantyTemplateUuid: null,
  warrantyTemplateOptionUuid: null,
  warrantyStartDate: "",
  hostAttributeValues: {},
  isInstalled: null,
  selectedExistingHostUuid: null,
  selectedExistingHostAddressKey: null,
  registrationComplete: false,
};

type Action =
  | { type: "UPDATE"; payload: Partial<WizardState> }
  | { type: "NEXT"; maxStep: number }
  | { type: "BACK" }
  | { type: "GO_TO"; step: number }
  | { type: "RESET" };

function reducer(state: WizardState, action: Action): WizardState {
  switch (action.type) {
    case "UPDATE":
      return { ...state, ...action.payload };
    case "NEXT":
      return { ...state, step: Math.min(state.step + 1, action.maxStep) };
    case "BACK":
      return { ...state, step: Math.max(state.step - 1, 0) };
    case "GO_TO":
      return { ...state, step: action.step };
    case "RESET":
      return { ...initialState };
    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Config help: which dedicated config pages drive each wizard segment. Used by
// the header "?" to summarize where the current step is configured.
// ---------------------------------------------------------------------------

const REGISTRATION_RETURN_LABEL = "New registration";

function configSectionsForSegment(
  segment: string | undefined,
): ConfigSectionKey[] {
  switch (segment) {
    case "product_type":
      return ["reg.product_type", "reg.steps"];
    case "host_context":
      return [
        "reg.host_fields",
        "reg.early_attributes",
        "reg.custom_fields",
        "reg.validation",
      ];
    case "host":
    case "host_product":
      return ["reg.host_fields", "reg.custom_fields", "reg.validation"];
    case "product":
      return ["reg.product_fields", "reg.custom_fields"];
    case "customer":
      return ["reg.customer_fields", "reg.custom_fields"];
    case "purchase_customer":
      return [
        "reg.customer_fields",
        "reg.purchase_fields",
        "reg.custom_fields",
      ];
    case "purchase":
      return ["reg.purchase_fields", "reg.custom_fields"];
    case "warranty":
      return ["reg.warranty", "reg.custom_fields"];
    case "registration_details":
      return ["reg.custom_fields", "reg.steps"];
    case "review":
      return ["reg.validation", "reg.approval", "reg.steps"];
    default:
      return ["reg.steps"];
  }
}

// The single most relevant config for a step, shown as an inline "Edit config"
// link on the step itself (in addition to the header "?" summary).
function primaryConfigSectionForSegment(
  segment: string | undefined,
): ConfigSectionKey | null {
  switch (segment) {
    case "product_type":
      return "reg.product_type";
    case "host_context":
    case "host":
    case "host_product":
      return "reg.host_fields";
    case "product":
      return "reg.product_fields";
    case "customer":
    case "purchase_customer":
      return "reg.customer_fields";
    case "purchase":
      return "reg.purchase_fields";
    case "warranty":
      return "reg.warranty";
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Segment-to-component mapping
// ---------------------------------------------------------------------------

function renderSegment(
  segment: string,
  common: {
    state: WizardState;
    update: (payload: Partial<WizardState>) => void;
    next: () => void;
    back: (() => void) | undefined;
    onStructurePickerActivation?: (activation: {
      active: boolean;
      ownedCodeNames: string[];
    }) => void;
  },
  ctx: {
    metadata: ReturnType<typeof useHoldingFormMetadata>["data"];
    /**
     * Registration-selectable product templates ("product types") offered on
     * the `product_type` step. Only populated/used when more than one exists.
     */
    selectableTemplates: {
      uuid: string;
      name: string;
      host_requirement?: string;
      product_count?: number;
    }[];
    templates: {
      uuid: string;
      name: string;
      attributes: {
        code_name: string;
        display_name: string;
        field_type: string;
        attribute_level: string;
        configuration_data?: Record<string, unknown>;
      }[];
      host_template: {
        uuid: string;
        name: string;
        attributes: {
          code_name: string;
          display_name: string;
          field_type: string;
          order: number;
          lookup_provider: string;
          is_prepopulated: boolean;
          maps_to_column: string;
        }[];
      } | null;
      host_requirement: string;
      early_registration_attributes: string[];
    }[];
    geoEnabled: boolean;
    warrantyTemplates: { uuid: string; options: { uuid: string }[] }[];
    /**
     * Registration-field code_names captured on the warranty step or
     * later. StepWarranty excludes them from the per-option availability
     * evaluation so typing in a warranty-step field (e.g. Solar PROtect
     * inputs) doesn't re-evaluate and flicker the warranty list.
     */
    warrantyContextExcludedFieldCodes: Set<string>;
    handleSubmit: () => Promise<SubmitRegistrationResult>;
    isSubmitting: boolean;
    continueDisabled?: boolean;
    continueHint?: string | null;
    submissionWarningHits?: RuleHit[];
    submissionBlockedMessage?: string | null;
    submissionApprovalHits?: ApprovalRuleHit[];
    enableExistingHostSelection: boolean;
    /**
     * When true, the product step renders a flat product list (no
     * category cards). Selecting a parent product drills into a
     * variant picker. Driven by `RegistrationSettings.flat_product_selection`.
     */
    flatProductSelection: boolean;
    /**
     * When set, the product step only shows products from the named product
     * template. Used when navigating from a claim form that requires a
     * specific product line (e.g. BFS, SunSuite). Null means no filtering.
     */
    productTemplateName?: string | null;
    hostSelectionDismissed: boolean;
    setHostSelectionDismissed: (v: boolean) => void;
    onReset: () => void;
    dataCaptureConfig?: DataCaptureConfig;
    earlyCaptureFields: string[];
    installerCapturedEarly: boolean;
    ruleContext?: {
      fields?: Record<string, unknown>;
      entities?: Record<string, Record<string, unknown>>;
      aggregates?: Record<string, unknown>;
      roles?: string[];
      permissions?: string[];
    };
    /**
     * Optional extra panel(s) to render alongside the step's own panels in
     * the shared `RegistrationStepGrid`. Used by the page to slot in the
     * shared `RegistrationFieldsPanel` so panels lay out as a 2-column grid
     * instead of stacking the registration fields below.
     */
    extraPanels?: React.ReactNode;
    /**
     * Registration-field nodes anchored to a host attribute via a
     * field-visibility rule, keyed by the host attribute's `code_name`.
     * On the `host_context` step these render inline immediately after
     * the trigger host attribute (e.g. `other_structure_type` directly
     * below `structure_type`) instead of at the bottom of the panel.
     */
    propertyAnchoredRegistrationFields?: Record<string, React.ReactNode>;
    segments: string[];
    returnTo?: string | null;
    claimSchemaKey?: string | null;
  },
): React.ReactNode {
  const { state, update, next, back, onStructurePickerActivation } = common;

  function renderBlockingState(
    title: string,
    description: string,
    options: { allowContinue?: boolean } = {},
  ) {
    return (
      <div className="max-w-[640px] rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-5">
        <div className="flex flex-col gap-3">
          <div>
            <p className="m-0 text-[10px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
              Wizard configuration
            </p>
            <h2 className="mt-2 text-[18px] text-[var(--ink)]">{title}</h2>
            <p className="mt-2 text-[13px] leading-6 text-[var(--ink-3)]">
              {description}
            </p>
          </div>
          <RegistrationStepActions
            onContinue={next}
            continueDisabled={!options.allowContinue}
            continueHint={
              options.allowContinue
                ? null
                : "Update the registration step order to continue."
            }
            onBack={back}
          />
        </div>
      </div>
    );
  }

  switch (segment) {
    case "product_type":
      return (
        <StepProductType
          templates={ctx.selectableTemplates}
          selectedUuid={state.productTemplateUuid}
          onSelect={(uuid) => {
            // Switching product type invalidates downstream selections that
            // were scoped to the previous template (product, captured host /
            // early attributes, warranty, geo eligibility), so clear them to
            // avoid carrying a mismatched product, warranty, or PSA result
            // into the new flow. Geo eligibility in particular is category-
            // aware (PSA filtering is opt-in per template), so a result
            // computed for the previous template must not leak into a
            // different one - otherwise the product step would show the old
            // template's "Showing N available" banner while its own catalogue
            // renders empty.
            const changed = uuid !== state.productTemplateUuid;
            update({
              productTemplateUuid: uuid,
              ...(changed
                ? {
                    selectedProduct: null,
                    earlyAttributes: {},
                    dynamicAttributes: {},
                    registrationFieldValues: {},
                    hostAttributeValues: {},
                    warrantyTemplateUuid: null,
                    warrantyTemplateOptionUuid: null,
                    geoEligibility: null,
                  }
                : {}),
            });
            next();
          }}
        />
      );

    case "host_context": {
      // Host-first flow: collect property + early attributes before product selection.
      // Use the first template that actually defines a host_template (and its
      // early_registration_attributes). A tenant can have catalog-only
      // templates with no host fields (e.g. component/accessory catalogs);
      // those must not drive this property step. Fall back to the first
      // template so single-template tenants behave exactly as before.
      const tmpl = resolveRegistrationTemplate(
        ctx.templates ?? [],
        state.productTemplateUuid,
      );
      const earlyCodeNames = tmpl?.early_registration_attributes ?? [];
      const earlyAttrs = (tmpl?.attributes ?? [])
        .filter(
          (a) =>
            a.attribute_level === "HOLDING" &&
            earlyCodeNames.includes(a.code_name),
        )
        .map((a) => ({
          uuid: a.code_name,
          code_name: a.code_name,
          display_name: a.display_name,
          field_type: a.field_type,
          configuration_data: a.configuration_data as
            { options?: { value: string; label: string }[] } | undefined,
        }));
      return (
        <StepHostContext
          state={state}
          update={update}
          next={next}
          back={back}
          continueDisabled={ctx.continueDisabled}
          continueHint={ctx.continueHint}
          hostTemplate={tmpl?.host_template ?? null}
          hostRequirement={tmpl?.host_requirement ?? "NONE"}
          earlyAttributes={earlyAttrs}
          geoEnabled={ctx.geoEnabled}
          propertyExtras={ctx.extraPanels}
          anchoredFieldsByHostAttr={ctx.propertyAnchoredRegistrationFields}
          dataCaptureConfig={ctx.dataCaptureConfig}
          earlyCaptureFields={ctx.earlyCaptureFields}
          ruleContext={ctx.ruleContext}
          onStructurePickerActivation={onStructurePickerActivation}
        />
      );
    }

    case "host_product": {
      // Backward-compatible combined Host + Product step. The dedicated
      // `product` and `host` segments are preferred for new flows, but tenants
      // (and the e2e seed) may still default to this combined layout.
      if (!ctx.metadata?.host_template) {
        return (
          <StepProductOnly
            state={state}
            update={update}
            next={next}
            continueDisabled={ctx.continueDisabled}
            continueHint={ctx.continueHint}
            metadata={ctx.metadata}
            extraPanels={ctx.extraPanels}
            flatProductSelection={ctx.flatProductSelection}
            productTemplateName={ctx.productTemplateName}
          />
        );
      }
      return (
        <StepHostProduct
          state={state}
          update={update}
          next={next}
          back={back}
          continueDisabled={ctx.continueDisabled}
          continueHint={ctx.continueHint}
          showProductPanel={true}
          showAdditionalDetails={true}
          metadata={ctx.metadata}
          extraPanels={ctx.extraPanels}
          flatProductSelection={ctx.flatProductSelection}
          dataCaptureConfig={ctx.dataCaptureConfig}
          earlyCaptureFields={ctx.earlyCaptureFields}
          onStructurePickerActivation={onStructurePickerActivation}
        />
      );
    }

    case "host": {
      if (!state.selectedProduct) {
        return renderBlockingState(
          "Product step required first",
          "Host details can only be collected after a product has been selected. Add the Product step before Host in Settings > Registration.",
        );
      }
      if (
        ctx.enableExistingHostSelection &&
        !ctx.hostSelectionDismissed &&
        state.consumerEmail &&
        ctx.metadata?.host_template &&
        !state.selectedExistingHostUuid
      ) {
        return (
          <StepHostSelection
            state={state}
            update={update}
            hostTemplate={ctx.metadata.host_template}
            onSelectExisting={() => next()}
            onCreateNew={() => ctx.setHostSelectionDismissed(true)}
          />
        );
      }
      if (ctx.metadata?.host_template) {
        return (
          <StepHostProduct
            state={state}
            update={update}
            next={next}
            continueDisabled={ctx.continueDisabled}
            continueHint={ctx.continueHint}
            showProductPanel={false}
            showAdditionalDetails={false}
            metadata={ctx.metadata}
            extraPanels={ctx.extraPanels}
            flatProductSelection={ctx.flatProductSelection}
            dataCaptureConfig={ctx.dataCaptureConfig}
            earlyCaptureFields={ctx.earlyCaptureFields}
            onStructurePickerActivation={onStructurePickerActivation}
          />
        );
      }
      return renderBlockingState(
        "No host details required",
        "The selected product does not define any host fields, so there is nothing to capture on this step.",
        { allowContinue: true },
      );
    }

    case "product":
      return (
        <StepProductOnly
          state={state}
          update={update}
          next={next}
          back={back}
          continueDisabled={ctx.continueDisabled}
          continueHint={ctx.continueHint}
          metadata={ctx.metadata}
          extraPanels={ctx.extraPanels}
          flatProductSelection={ctx.flatProductSelection}
          productTemplateName={ctx.productTemplateName}
        />
      );

    case "purchase_customer":
      // Backward-compatible combined Purchase + Customer step. New flows
      // should split these into separate `customer` and `purchase` segments,
      // but legacy defaults still rely on the combined layout.
      return (
        <StepPurchaseCustomer
          state={state}
          update={update}
          next={next}
          back={back}
          continueDisabled={ctx.continueDisabled}
          continueHint={ctx.continueHint}
          dataCaptureConfig={ctx.dataCaptureConfig}
          earlyCaptureFields={ctx.earlyCaptureFields}
          metadata={ctx.metadata}
          extraPanels={ctx.extraPanels}
        />
      );

    case "customer":
      return (
        <StepCustomer
          state={state}
          update={update}
          next={next}
          back={back}
          continueDisabled={ctx.continueDisabled}
          continueHint={ctx.continueHint}
          dataCaptureConfig={ctx.dataCaptureConfig}
          metadata={ctx.metadata}
          earlyCaptureFields={ctx.earlyCaptureFields}
          installerCapturedEarly={ctx.installerCapturedEarly}
          extraPanels={ctx.extraPanels}
        />
      );

    case "purchase":
      return (
        <StepPurchase
          state={state}
          update={update}
          next={next}
          back={back}
          continueDisabled={ctx.continueDisabled}
          continueHint={ctx.continueHint}
          metadata={ctx.metadata}
          dataCaptureConfig={ctx.dataCaptureConfig}
          extraPanels={ctx.extraPanels}
        />
      );

    case "warranty":
      if (!state.selectedProduct) {
        return renderBlockingState(
          "Product step required first",
          "Warranty selection depends on the chosen product. Add the Product step before Warranty in Settings > Registration.",
        );
      }
      return (
        <StepWarranty
          state={state}
          update={update}
          next={next}
          back={back}
          continueDisabled={ctx.continueDisabled}
          continueHint={ctx.continueHint}
          warrantyTemplates={ctx.warrantyTemplates}
          excludedAvailabilityFieldCodes={ctx.warrantyContextExcludedFieldCodes}
          filterSummary={ctx.metadata?.warranty_filter_summary ?? null}
          extraPanels={ctx.extraPanels}
        />
      );

    case "registration_details":
      return (
        <RegistrationStepActions
          onContinue={next}
          continueDisabled={ctx.continueDisabled}
          continueHint={ctx.continueHint}
          onBack={back}
        />
      );

    case "review":
      return (
        <StepReview
          state={state}
          update={update}
          next={next}
          back={back}
          metadata={ctx.metadata}
          warrantyTemplates={ctx.warrantyTemplates}
          onSubmit={ctx.handleSubmit}
          isSubmitting={ctx.isSubmitting}
          onReset={ctx.onReset}
          submissionWarningHits={ctx.submissionWarningHits}
          submissionBlockedMessage={ctx.submissionBlockedMessage}
          submissionApprovalHits={ctx.submissionApprovalHits}
          segments={ctx.segments}
          returnTo={ctx.returnTo}
          claimSchemaKey={ctx.claimSchemaKey}
        />
      );

    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Loading skeleton - rendered while real registration settings are still
// loading. Mirrors the standard 2-column step grid so swapping in the
// real step content doesn't cause a layout shift.
// ---------------------------------------------------------------------------

function WizardStepSkeleton() {
  return (
    <div aria-busy="true" className="flex flex-col gap-6">
      <div className="grid grid-cols-1 items-stretch gap-6 lg:grid-cols-2">
        {[0, 1].map((i) => (
          <div
            key={i}
            className="flex flex-col gap-4 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-5"
          >
            <div className="flex items-center gap-2">
              <div className="h-[14px] w-[14px] rounded-[3px] bg-[var(--skeleton-base)]" />
              <div className="h-[10px] w-[120px] rounded-[2px] bg-[var(--skeleton-base)]" />
            </div>
            <div className="flex flex-col gap-3">
              <div className="h-9 w-full rounded-[4px] bg-[var(--skeleton-base)]" />
              <div className="h-9 w-[80%] rounded-[4px] bg-[var(--skeleton-base)]" />
              <div className="h-9 w-[60%] rounded-[4px] bg-[var(--skeleton-base)]" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function NewRegistrationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const locationState = location.state as NewRegistrationLocationState | null;
  const propertyRegistrationPrefill =
    locationState?.propertyRegistrationPrefill;
  const returnTo = locationState?.returnTo ?? null;
  const locationClaimSchemaKey = locationState?.claimSchemaKey ?? null;
  // Accept productTemplateName from navigation state (coming from claims form)
  // OR from a URL query param (?productTemplateName=...) for direct navigation.
  const locationProductTemplateName =
    locationState?.productTemplateName ??
    searchParams.get("productTemplateName") ??
    null;
  const { currency: orgCurrency, formatDateLong } = useFormatting();
  // Seed `priceCurrency` with the org's default currency so the wizard
  // opens in the right currency before the product/transaction template
  // overrides it.
  // Resume an in-progress registration from cache: restore BOTH the captured
  // values AND the step the user was on. The cache only survives a hard page
  // reload (see `useWizardCachePersistence` - it is cleared on in-app
  // navigation), so restoring the step lands the user back exactly where the
  // reload interrupted them. Skipped when the entry point carries explicit
  // prefill/product-template intent (those override a stale cached draft).
  const cacheKey = useWizardCacheKey("new-registration");
  const hasEntryPointIntent = Boolean(
    propertyRegistrationPrefill ||
    locationProductTemplateName ||
    locationClaimSchemaKey,
  );
  const [state, dispatch] = useReducer(
    reducer,
    undefined as unknown as WizardState,
    () => {
      if (!hasEntryPointIntent) {
        const cached = readWizardCache<WizardState>(cacheKey);
        // A completed registration is never cached (cleared on submit), but guard
        // against a stray flag so resume can't land on the success screen. The
        // step is clamped at render time (see `currentStepIndex`) so a step that
        // is now out of range after a config change still renders safely.
        //
        // Merge over `initialState` (not a bare `{ ...cached }`) so a draft written
        // by an older build that predates a newly-added state field still gets a
        // sane default for it instead of `undefined`; the cached values (and step)
        // win via the trailing spread.
        if (cached && !cached.registrationComplete) {
          return {
            ...initialState,
            priceCurrency: orgCurrency || initialState.priceCurrency,
            ...cached,
          };
        }
      }
      return {
        ...initialState,
        priceCurrency: orgCurrency || initialState.priceCurrency,
        // Immediately seed from property prefill so the form renders pre-filled
        // on the very first paint, with no waiting for templates to load.
        ...(propertyRegistrationPrefill
          ? {
              hostAttributeValues: {
                ...initialState.hostAttributeValues,
                ...(propertyRegistrationPrefill.hostAttributeValues ?? {}),
                ...(propertyRegistrationPrefill.hostColumnValues ?? {}),
              },
              selectedExistingHostUuid:
                propertyRegistrationPrefill.hostUuid ?? null,
              isInstalled: true,
              consumerEmail:
                propertyRegistrationPrefill.customerPrefill?.email ?? "",
              consumerFirstName:
                propertyRegistrationPrefill.customerPrefill?.firstName ?? "",
              consumerLastName:
                propertyRegistrationPrefill.customerPrefill?.lastName ?? "",
              consumerPhone:
                propertyRegistrationPrefill.customerPrefill?.phone ?? "",
              ...(propertyRegistrationPrefill.earlyAttributesPrefill &&
              Object.keys(propertyRegistrationPrefill.earlyAttributesPrefill)
                .length > 0
                ? {
                    earlyAttributes:
                      propertyRegistrationPrefill.earlyAttributesPrefill,
                  }
                : {}),
            }
          : {}),
      };
    },
  );
  // Persist the in-progress wizard so leaving and re-entering "New
  // Registration" resumes on the same step with the same values. Cleared once
  // the registration is submitted (`registrationComplete`).
  useWizardCachePersistence(cacheKey, state, state.registrationComplete);

  const [serverStepValidation, setServerStepValidation] =
    useState<ServerStepValidation | null>(null);
  const [isValidatingStepRules, setIsValidatingStepRules] = useState(false);
  const [pendingAddressDuplicateWarning, setPendingAddressDuplicateWarning] =
    useState<PendingAddressDuplicateWarning>(null);

  const update = useCallback((payload: Partial<WizardState>) => {
    setServerStepValidation(null);
    dispatch({ type: "UPDATE", payload });
  }, []);

  // Fetch registration settings (segment order). The query exposes
  // placeholder defaults synchronously so other consumers don't have to
  // null-check; the wizard, however, must wait for the real tenant
  // settings before rendering anything that depends on them. Without
  // gating, tenants whose `segment_order` or `flat_product_selection`
  // differ from the defaults briefly render the legacy categorised
  // product step before re-rendering with their real flow - see the
  // "category step flashes on load" bug.
  const { data: regSettings, isPlaceholderData: regSettingsPlaceholder } =
    useRegistrationSettings();
  // Fetch templates (for host_context step which needs template-level data
  // before product selection, and to decide whether the "product type"
  // selection step is needed).
  const { data: templates, isLoading: templatesLoading } =
    useProductTemplates();

  // The "product types" a user can register against. When a tenant has more
  // than one selectable product template we insert an explicit selection step
  // so the user - not alphabetical ordering - decides which template drives
  // the flow. Claim-only templates (e.g. BFS) are provisioned through their
  // claim flow and must not appear in the standalone product-type chooser;
  // they stay reachable via the claim round-trip, which targets the template
  // by name. (Catalog-only "component" templates that pollute this list should
  // be remodelled as entities - see follow-up ticket - rather than filtered.)
  const selectableTemplates = useMemo(
    () => (templates ?? []).filter((t) => !t.registration_via_claim_only),
    [templates],
  );
  const needsTemplateChoice = selectableTemplates.length > 1;

  // Scope the product step to the template the user picked in step 1.
  //
  // The product picker narrows to a single template's catalogue via
  // `productTemplateName`. An external entry point (a claim-form round-trip)
  // supplies it through navigation/URL; otherwise it must come from the
  // explicit product-type choice the user just made, so the picker shows ONLY
  // that category's products - not the whole tenant catalogue. Derive it ONLY
  // from an explicit `state.productTemplateUuid`: a tenant with no product-type
  // step (whole-catalogue browse) leaves this null and keeps the full picker.
  const selectedProductTemplateName = useMemo(() => {
    if (!state.productTemplateUuid) return null;
    return (
      (templates ?? []).find((t) => t.uuid === state.productTemplateUuid)
        ?.name ?? null
    );
  }, [templates, state.productTemplateUuid]);
  const effectiveProductTemplateName =
    locationProductTemplateName ?? selectedProductTemplateName;

  // Hold the wizard on the skeleton until BOTH the segment order (tenant
  // settings) and the product templates have resolved. Otherwise `segments`
  // briefly thrashes as defaults -> real segment_order -> +product_type,
  // which can flash the wrong first step (and detach buttons mid-click).
  const wizardLoading = regSettingsPlaceholder || templatesLoading;

  const baseSegments = useMemo(
    () =>
      regSettings?.segment_order ?? [
        "product",
        "host",
        "purchase",
        "customer",
        "review",
      ],
    [regSettings],
  );
  const mergeReplacementSelected =
    state.duplicateAddressResolution?.decision === "merge" &&
    Boolean(state.duplicateAddressResolution.selectedHoldingUuid);
  const skipCustomerStep =
    mergeReplacementSelected ||
    !!propertyRegistrationPrefill?.customerPrefill?.email;
  const segments = useMemo(() => {
    const base = skipCustomerStep
      ? baseSegments.filter((segment) => segment !== "customer")
      : baseSegments;
    // Prepend the product-type chooser only when the tenant has more than
    // one selectable template. Single-template tenants never see it and the
    // template is resolved implicitly (legacy behaviour).
    return needsTemplateChoice ? ["product_type", ...base] : base;
  }, [baseSegments, skipCustomerStep, needsTemplateChoice]);
  const currentStepIndex = Math.min(
    state.step,
    Math.max(segments.length - 1, 0),
  );
  const currentSegment = segments[currentStepIndex];

  // Host + holding attributes collected on earlier wizard steps drive
  // warranty resolution. Memoise so the metadata query cache key is
  // stable across re-renders.
  const installationDate = useMemo(() => {
    const raw = state.earlyAttributes?.installation_date;
    return typeof raw === "string" && raw ? raw : null;
  }, [state.earlyAttributes]);

  // Registration year drives the component product picker's year filter.
  const registrationYear = useMemo(() => {
    if (!installationDate) return null;
    // Extract the year directly from the leading YYYY of the ISO string.
    // Parsing via `new Date(...).getFullYear()` evaluates a date-only ISO
    // string (parsed as UTC) in the local timezone, which can shift the year
    // back a day for users behind UTC (e.g. 2024-01-01 → 2023). String slice
    // is timezone-safe.
    const match = /^(\d{4})/.exec(String(installationDate));
    if (!match) return null;
    const year = Number.parseInt(match[1], 10);
    return Number.isFinite(year) ? year : null;
  }, [installationDate]);

  // Resource-lookup providers available to ENTITY_SEARCH fields in the wizard.
  // The `part` provider searches `part` entity records (Tier 2 reference data -
  // "components" for OC - not products), scoped by the category passed via the
  // field's `provider_params` and by the registration year closed over here, so
  // a single provider serves every component/part picker field.
  const queryClient = useQueryClient();
  // Shared admin registry (partnership, ...) so config-authored ENTITY_SEARCH
  // holding attributes (e.g. a partner/dealer picker with
  // `lookup_provider: "partnership"`) resolve on the wizard too - the local
  // `part` provider is layered on top.
  const sharedLookupProviders = useResourceLookupProviders();
  const resourceLookupProviders = useMemo<ResourceLookupRegistry>(
    () => ({
      ...sharedLookupProviders,
      part: {
        // Routed through the React Query cache (SEARCH policy) so re-opening the
        // product/part picker or repeating a term paints from cache instead of
        // re-hitting the server on every keystroke.
        search: (
          term: string,
          params?: Record<string, unknown>,
        ): Promise<ResourceOption[]> => {
          const category = params?.category as string | undefined;
          return queryClient.fetchQuery({
            queryKey: [
              "resource-search",
              "part",
              term,
              category ?? null,
              registrationYear ?? null,
            ],
            queryFn: async (): Promise<ResourceOption[]> => {
              const queryParams: Record<string, string> = {
                search: term,
                page_size: "20",
              };
              if (category) queryParams["filter[category]"] = category;
              if (registrationYear) {
                queryParams["filter[installation_year]"] =
                  String(registrationYear);
              }
              const { data } = await api.get<{
                results?: {
                  record_uuid: string;
                  label: string;
                  values?: Record<string, unknown>;
                }[];
              }>("/entities/types/part/records/search/", {
                params: queryParams,
              });
              return (data.results ?? []).map((r): ResourceOption => {
                const line = (r.values?.product_line as string) ?? "";
                const name = r.label || line;
                return {
                  uuid: r.record_uuid,
                  name,
                };
              });
            },
          });
        },
      },
    }),
    [registrationYear, queryClient, sharedLookupProviders],
  );

  const { data: registrationFields } = useRegistrationFields();

  // Registration fields captured on the warranty step or later (e.g. the
  // Solar PROtect requirement fields) are pure data capture - no warranty
  // availability or filter rule reads them, and they're collected at/after
  // warranty selection so they can't gate which warranties are available.
  // They must NOT feed the warranty-resolution context: both the metadata
  // query (`host_attrs` cache key) AND the per-option availability
  // evaluation key off it, so without this guard every keystroke in a
  // warranty-step field refetches and flickers the warranty cards on the
  // left. We only surface fields collected strictly before the warranty
  // step into the resolver context.
  const warrantyContextExcludedFieldCodes = useMemo(() => {
    const excluded = new Set<string>();
    if (!registrationFields) return excluded;
    const warrantyIdx = segments.indexOf("warranty");
    if (warrantyIdx < 0) return excluded;
    for (const field of registrationFields) {
      const step = field.capture_step ?? null;
      // Null capture_step fields render on the last content step (before
      // review), which is at/after warranty - exclude them too.
      const stepIdx = step ? segments.indexOf(step) : segments.length - 2;
      if (stepIdx >= warrantyIdx) excluded.add(field.code_name);
    }
    return excluded;
  }, [registrationFields, segments]);

  const warrantyContextRegistrationFieldValues = useMemo(() => {
    if (warrantyContextExcludedFieldCodes.size === 0) {
      return state.registrationFieldValues;
    }
    const filtered: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(state.registrationFieldValues || {})) {
      if (!warrantyContextExcludedFieldCodes.has(k)) filtered[k] = v;
    }
    return filtered;
  }, [state.registrationFieldValues, warrantyContextExcludedFieldCodes]);

  const hostAttrsForMetadata = useMemo(() => {
    return mirrorRegistrationFieldsToHost(
      state.hostAttributeValues,
      warrantyContextRegistrationFieldValues,
      { dropEmpty: true },
    );
  }, [state.hostAttributeValues, warrantyContextRegistrationFieldValues]);

  // Holding-level attributes (Pitch of Roof, Roof Total Squares, OC
  // Components, …) live on ``state.dynamicAttributes``. The wizard
  // collects them on the product step BEFORE warranty selection, so
  // they're available as resolver input for any rule that gates on a
  // holding-scoped field. ``state.earlyAttributes`` covers the same
  // shape for fields a tenant marks as early-collected, so we union
  // both - early attrs win on key collision since they were chosen
  // explicitly to surface ahead of the product step.
  const holdingAttrsForMetadata = useMemo(() => {
    const merged: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(state.dynamicAttributes ?? {})) {
      if (v === undefined || v === null || v === "") continue;
      merged[k] = v;
    }
    for (const [k, v] of Object.entries(state.earlyAttributes ?? {})) {
      if (v === undefined || v === null || v === "") continue;
      merged[k] = v;
    }
    return merged;
  }, [state.dynamicAttributes, state.earlyAttributes]);

  // Fetch form metadata when product is selected. Pass installation date
  // + host attributes + holding attributes so the server returns a
  // pre-filtered template list and pre-assigned duration options.
  const { data: metadata } = useHoldingFormMetadata(
    state.selectedProduct?.uuid ?? null,
    {
      installation_date: installationDate,
      partnership_uuid: state.partnershipUuid,
      host_attrs: hostAttrsForMetadata,
      holding_attrs: holdingAttrsForMetadata,
      product_template_uuid:
        state.productTemplateUuid ??
        state.selectedProduct?.product_template?.uuid ??
        null,
    },
  );

  const [propertyPrefillApplied, setPropertyPrefillApplied] = useState(false);

  // Re-apply the property prefill when the chosen product template changes:
  // switching product type clears hostAttributeValues, and the prefill is
  // mapped to the new template's host_template attributes, so the one-shot
  // guard must reset for the prefill effect to run again.
  useEffect(() => {
    setPropertyPrefillApplied(false);
  }, [state.productTemplateUuid]);
  const geoEnabled = useFeatureFlag(FLAGS.GEOGRAPHIC_AVAILABILITY);
  const registrationFieldsEnabled = useFeatureFlag(FLAGS.REGISTRATION_FIELDS);
  const rulesEnabled = useFeatureFlag(FLAGS.REGISTRATION_RULES);
  const exceptionsEnabled = useFeatureFlag(FLAGS.EXCEPTIONS);
  const { data: holdingEntities = [] } = useEntityTypes(
    { key: "holding" },
    { enabled: rulesEnabled },
  );
  const holdingEntity = holdingEntities[0] ?? null;
  const { data: orgContext } = useOrg();
  const { data: selectedPartnership } = usePartnershipDetail(
    state.partnershipUuid,
  );

  useEffect(() => {
    if (!propertyRegistrationPrefill || propertyPrefillApplied || !templates) {
      return;
    }

    const registrationTemplate = resolveRegistrationTemplate(
      templates,
      state.productTemplateUuid,
    );
    const hostTemplateAttributes =
      registrationTemplate?.host_template?.attributes ?? [];
    const hostAttributeValues = mergePropertyHostPrefill(
      propertyRegistrationPrefill,
      hostTemplateAttributes,
    );
    const cp = propertyRegistrationPrefill.customerPrefill;
    const ep = propertyRegistrationPrefill.earlyAttributesPrefill;
    update({
      hostAttributeValues: {
        ...state.hostAttributeValues,
        ...hostAttributeValues,
      },
      selectedExistingHostUuid: propertyRegistrationPrefill.hostUuid ?? null,
      isInstalled: true,
      // Re-apply customer prefill so it survives if templates loaded after
      // the reducer was first seeded (e.g. on a slow connection).
      ...(cp?.email ? { consumerEmail: cp.email } : {}),
      ...(cp?.firstName ? { consumerFirstName: cp.firstName } : {}),
      ...(cp?.lastName ? { consumerLastName: cp.lastName } : {}),
      ...(cp?.phone ? { consumerPhone: cp.phone } : {}),
      // Re-apply early attribute prefill (pitch, stories, etc.) so values
      // survive if templates loaded after the reducer was first seeded.
      ...(ep && Object.keys(ep).length > 0
        ? { earlyAttributes: { ...state.earlyAttributes, ...ep } }
        : {}),
    });
    setPropertyPrefillApplied(true);
  }, [
    propertyPrefillApplied,
    propertyRegistrationPrefill,
    state.productTemplateUuid,
    state.hostAttributeValues,
    state.earlyAttributes,
    templates,
    update,
  ]);

  // Build step labels dynamically from segments
  const stepLabels = useMemo(
    () => segments.map((seg) => getSegmentLabel(seg)),
    [segments],
  );

  const {
    data: legacyValidationRules = [],
    isLoading: validationRulesLoading,
  } = useValidationRules({ enabled: rulesEnabled, scope: "registration_step" });
  const { data: holdingValidationRules = [] } = useValidationRules({
    enabled: rulesEnabled && Boolean(holdingEntity?.uuid),
    scope: "entity_registration",
    entityType: holdingEntity?.uuid,
  });
  const { data: approvalRules = [] } = useApprovalRules({
    enabled: rulesEnabled,
  });
  const back = useCallback(() => dispatch({ type: "BACK" }), []);

  // Existing host selection: track whether user dismissed the selection panel
  const enableExistingHostSelection =
    regSettings?.enable_existing_host_selection ?? false;
  const flatProductSelection = regSettings?.flat_product_selection ?? false;
  const [hostSelectionDismissed, setHostSelectionDismissed] = useState(false);

  // Reset dismissed state when navigating back to the host step
  useEffect(() => {
    // If user navigated back and previously had an existing host, reset
    if (state.selectedExistingHostUuid) {
      setHostSelectionDismissed(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.step]);

  const warrantyTemplates = useMemo(
    () => metadata?.warranty_templates ?? [],
    [metadata?.warranty_templates],
  );

  useEffect(() => {
    if (warrantyTemplates.length !== 1 || state.warrantyTemplateUuid) return;
    const only = warrantyTemplates[0];
    // Patch both fields in a single ``update`` so the reducer commits
    // them atomically. Splitting the dispatch (one for the class, then
    // a second for the option) leaves a one-tick window where the
    // step-level "drop class if not visible" guard can clear the class
    // before the option dispatch lands, and the user ends up with
    // ``selectedClassUuid`` set, ``selectedOptionUuid`` null, and the
    // Continue button silently rejecting the click because
    // ``isWarrantySelectionResolved`` is false. Use the same resolver
    // the step-level fallback uses so a stale ``pre_assigned_option_uuid``
    // (one that doesn't match any option in the payload) falls through
    // to the first available option instead of poisoning the selection.
    const patch: Partial<WizardState> = { warrantyTemplateUuid: only.uuid };
    const optionUuid = getDefaultWarrantyOptionUuid(only);
    if (optionUuid) {
      patch.warrantyTemplateOptionUuid = optionUuid;
    }
    update(patch);
  }, [warrantyTemplates, state.warrantyTemplateUuid, update]);

  // Submit
  const createHolding = useCreateHolding();

  const buildRuleContext = useCallback(() => {
    const consumerName = [state.consumerFirstName, state.consumerLastName]
      .filter(Boolean)
      .join(" ");
    const today = new Date().toISOString().slice(0, 10);
    const fields: Record<string, unknown> = {
      consumer_email: state.consumerEmail || "",
      consumer_name: consumerName,
      consumer_phone: state.consumerPhone || "",
      consumer_address:
        state.address?.formatted_address ?? state.address?.full_address ?? "",
      registration_date: today,
      transaction_date: state.purchaseDate,
      transaction_price: state.purchasePrice
        ? Number(state.purchasePrice)
        : undefined,
      transaction_proof_of_purchase: state.transactionData.proof_of_purchase,
      ...state.earlyAttributes,
      ...state.dynamicAttributes,
      ...state.registrationFieldValues,
    };
    const entities: Record<string, Record<string, unknown>> = {};
    const hostMirror = buildHostRuleEntity(
      state.hostAttributeValues,
      state.registrationFieldValues,
      metadata?.host_template?.attributes ?? [],
    );
    if (Object.keys(hostMirror).length > 0) {
      entities.host = hostMirror;
      entities.property = hostMirror;
    }
    entities.holding = {
      ...fields,
    };
    if (state.selectedProduct) {
      entities.product = {
        uuid: state.selectedProduct.uuid,
        cached_short_name: state.selectedProduct.cached_short_name,
        product_type: state.selectedProduct.product_type,
        ...(typeof state.selectedProduct.product_data === "object" &&
        state.selectedProduct.product_data !== null
          ? state.selectedProduct.product_data
          : {}),
      };
    }
    if (selectedPartnership) {
      entities.contractor = {
        uuid: selectedPartnership.uuid,
        organisation_uuid: selectedPartnership.downstream_organisation.uuid,
        organisation_name: selectedPartnership.downstream_organisation.name,
        ...(selectedPartnership.role_uuid
          ? {
              role_uuid: selectedPartnership.role_uuid,
              role_name: selectedPartnership.role_name,
            }
          : {}),
        ...Object.fromEntries(
          (selectedPartnership.attribute_values ?? []).map((attr) => [
            attr.code_name,
            attr.value,
          ]),
        ),
      };
    }
    entities.consumer = {
      email: state.consumerEmail || "",
      first_name: state.consumerFirstName || "",
      last_name: state.consumerLastName || "",
      phone_number: state.consumerPhone || "",
      name: consumerName,
    };
    if (state.warrantyTemplateUuid) {
      const selectedTemplate = warrantyTemplates.find(
        (t) => t.uuid === state.warrantyTemplateUuid,
      );
      if (selectedTemplate) {
        entities.warranty_template = {
          uuid: selectedTemplate.uuid,
          name: selectedTemplate.name,
          warranty_type: selectedTemplate.warranty_type,
          ...selectedTemplate.rule_entity,
        };
      }
      if (state.warrantyTemplateOptionUuid) {
        entities.warranty_option = {
          uuid: state.warrantyTemplateOptionUuid,
        };
      }
    }
    return {
      fields,
      entities,
      permissions: orgContext?.permissions ?? [],
      roles: [],
    };
  }, [
    metadata?.host_template?.attributes,
    orgContext?.permissions,
    selectedPartnership,
    state,
    warrantyTemplates,
  ]);

  const currentStepRules = useMemo(() => {
    // The product-type chooser has no fields, so no validation rules apply
    // to it (including global trigger_step=null rules).
    if (!rulesEnabled || !currentSegment || currentSegment === "product_type")
      return [];
    return [...legacyValidationRules, ...holdingValidationRules].filter(
      (rule) =>
        rule.is_active &&
        (rule.trigger_step === currentSegment || rule.trigger_step === null),
    );
  }, [
    currentSegment,
    holdingValidationRules,
    legacyValidationRules,
    rulesEnabled,
  ]);

  const ruleContext = useMemo(() => buildRuleContext(), [buildRuleContext]);

  const localStepValidation = useMemo(() => {
    if (!rulesEnabled || currentStepRules.length === 0) {
      return { hits: [], unsupportedRuleUuids: [] };
    }
    return evaluateValidationRulesLocally(currentStepRules, ruleContext);
  }, [currentStepRules, ruleContext, rulesEnabled]);

  const serverStepHits = useMemo(() => {
    if (serverStepValidation?.step !== currentSegment) return [];
    return serverStepValidation.hits;
  }, [currentSegment, serverStepValidation]);

  const currentStepRuleHits = useMemo(
    () => mergeRuleHits(localStepValidation.hits, serverStepHits),
    [localStepValidation.hits, serverStepHits],
  );

  const currentStepRuleByUuid = useMemo(
    () => new Map(currentStepRules.map((rule) => [rule.uuid, rule])),
    [currentStepRules],
  );

  const addressDuplicateConfirmationKey = useMemo(
    () => addressDuplicateKey(ruleContext),
    [ruleContext],
  );

  // WPS-137: a pinned existing-host link (from the duplicate-address "continue
  // with this property" decision, the by-consumer existing-host picker, or a
  // property prefill) is tied to one property address. If the operator changes
  // the property address afterwards, drop the stale link so the warranty is not
  // registered against the previous address' property. `reconcilePinnedHost`
  // captures the pinned address on first sight and resets once it diverges.
  useEffect(() => {
    const patch = reconcilePinnedHost(
      {
        selectedExistingHostUuid: state.selectedExistingHostUuid,
        selectedExistingHostAddressKey: state.selectedExistingHostAddressKey,
      },
      addressDuplicateConfirmationKey,
    );
    if (patch) {
      dispatch({ type: "UPDATE", payload: patch });
    }
  }, [
    addressDuplicateConfirmationKey,
    state.selectedExistingHostUuid,
    state.selectedExistingHostAddressKey,
  ]);

  const visibleStepRuleHits = useMemo(
    () =>
      currentStepRuleHits.filter(
        (hit) => !isNonBlockingAddressDuplicateHit(hit, currentStepRuleByUuid),
      ),
    [currentStepRuleByUuid, currentStepRuleHits],
  );

  const currentStepBlockingHits = useMemo(
    () => currentStepRuleHits.filter((hit) => hit.severity === "BLOCK"),
    [currentStepRuleHits],
  );

  const ruleContinueDisabled =
    (rulesEnabled && validationRulesLoading) ||
    isValidatingStepRules ||
    currentStepBlockingHits.length > 0;

  // The rule alert above the step already gives the full message, so the
  // hint by Continue only says why the step did not move on.
  const ruleContinueHint =
    currentStepBlockingHits.length > 0
      ? t("registration.continueBlockedSeeAbove", {
          count: currentStepBlockingHits.length,
          defaultValue_one: "Can't continue yet. See the issue above.",
          defaultValue_other:
            "Can't continue yet. See the {{count}} issues above.",
        })
      : null;

  const evaluateUnsupportedCurrentStepRules = useCallback(async (): Promise<
    RuleHit[]
  > => {
    if (
      !rulesEnabled ||
      !currentSegment ||
      localStepValidation.unsupportedRuleUuids.length === 0
    ) {
      return [];
    }

    setIsValidatingStepRules(true);
    try {
      const unsupported = new Set(localStepValidation.unsupportedRuleUuids);
      const unsupportedRules = currentStepRules.filter((rule) =>
        unsupported.has(rule.uuid),
      );
      const requests: Array<Promise<RuleHit[]>> = [];
      if (unsupportedRules.some((rule) => rule.scope === "registration_step")) {
        requests.push(
          evaluateValidationRules({
            step: currentSegment,
            context: ruleContext,
            scope: "registration_step",
          })
            .then((result) => result.hits)
            .catch((err: unknown) => {
              const response = (
                err as {
                  response?: {
                    status?: number;
                    data?: { result?: EvaluateResult };
                  };
                }
              )?.response;
              if (response?.status === 422) {
                return response.data?.result?.hits ?? [];
              }
              throw err;
            }),
        );
      }
      if (
        holdingEntity?.uuid &&
        unsupportedRules.some((rule) => rule.scope === "entity_registration")
      ) {
        requests.push(
          evaluateValidationRules({
            step: currentSegment,
            context: ruleContext,
            scope: "entity_registration",
            entity_type: holdingEntity.uuid,
          })
            .then((result) => result.hits)
            .catch((err: unknown) => {
              const response = (
                err as {
                  response?: {
                    status?: number;
                    data?: { result?: EvaluateResult };
                  };
                }
              )?.response;
              if (response?.status === 422) {
                return response.data?.result?.hits ?? [];
              }
              throw err;
            }),
        );
      }
      const serverHits = (await Promise.all(requests))
        .flat()
        .filter((hit) => unsupported.has(hit.rule_uuid));
      setServerStepValidation({
        step: currentSegment,
        hits: serverHits,
      });
      return serverHits;
    } catch (err: unknown) {
      const response = (
        err as {
          response?: { status?: number; data?: { result?: EvaluateResult } };
        }
      )?.response;
      if (response?.status === 403 || response?.status === 404) {
        setServerStepValidation({
          step: currentSegment,
          hits: [],
        });
        return [];
      }

      if (response?.status !== 403 && response?.status !== 404) {
        console.error("Validation rule refresh failed", err);
      }
      setServerStepValidation({
        step: currentSegment,
        hits: [],
      });
      return [];
    } finally {
      setIsValidatingStepRules(false);
    }
  }, [
    currentSegment,
    currentStepRules,
    holdingEntity?.uuid,
    localStepValidation.unsupportedRuleUuids,
    ruleContext,
    rulesEnabled,
  ]);

  useEffect(() => {
    if (!rulesEnabled || !isDuplicateAddressReviewSegment(currentSegment))
      return;

    const handle = window.setTimeout(() => {
      const nextIssues = syncDuplicateAddressIssueState(
        state.registrationIssues,
        {
          segment: currentSegment,
          addressKey: addressDuplicateConfirmationKey,
          hits: currentStepRuleHits,
          ruleByUuid: currentStepRuleByUuid,
          resolvedAddressKey: state.duplicateAddressResolution?.addressKey,
        },
      );
      if (!registrationIssuesEqual(state.registrationIssues, nextIssues)) {
        dispatch({
          type: "UPDATE",
          payload: { registrationIssues: nextIssues },
        });
      }
    }, 0);

    return () => window.clearTimeout(handle);
  }, [
    addressDuplicateConfirmationKey,
    currentSegment,
    currentStepRuleByUuid,
    currentStepRuleHits,
    rulesEnabled,
    state.duplicateAddressResolution?.addressKey,
    state.registrationIssues,
  ]);

  // When arriving at the review step, eagerly run server-side rule evaluation
  // once so that warning hits (e.g. late-registration date_diff rules) are
  // visible before the user clicks "Confirm & Register". This is intentionally
  // a step-entry one-shot, not a per-change background loop.
  const evaluatedForSegmentRef = useRef<string | null>(null);
  useEffect(() => {
    if (currentSegment !== "review") {
      evaluatedForSegmentRef.current = null;
      return;
    }
    if (
      !rulesEnabled ||
      localStepValidation.unsupportedRuleUuids.length === 0
    ) {
      return;
    }
    if (evaluatedForSegmentRef.current === currentSegment) return;
    evaluatedForSegmentRef.current = currentSegment;
    void evaluateUnsupportedCurrentStepRules();
  }, [
    currentSegment,
    evaluateUnsupportedCurrentStepRules,
    localStepValidation.unsupportedRuleUuids.length,
    rulesEnabled,
  ]);

  const next = useCallback(async () => {
    if (!currentSegment) return;

    // The product-type chooser is a pure pick-and-advance step with no
    // fields of its own. It must not be gated by step validation - in
    // particular global (trigger_step=null) rules apply to every segment
    // and would evaluate against an empty context here, blocking advance.
    if (currentSegment === "product_type") {
      dispatch({ type: "NEXT", maxStep: segments.length - 1 });
      return;
    }

    if (ruleContinueDisabled) {
      return;
    }

    const serverHits = await evaluateUnsupportedCurrentStepRules();
    const stepHits = mergeRuleHits(localStepValidation.hits, serverHits);
    const nextIssues = syncDuplicateAddressIssueState(
      state.registrationIssues,
      {
        segment: currentSegment,
        addressKey: addressDuplicateConfirmationKey,
        hits: stepHits,
        ruleByUuid: currentStepRuleByUuid,
        resolvedAddressKey: state.duplicateAddressResolution?.addressKey,
      },
    );

    if (!registrationIssuesEqual(state.registrationIssues, nextIssues)) {
      dispatch({
        type: "UPDATE",
        payload: { registrationIssues: nextIssues },
      });
    }

    if (stepHits.some((hit) => hit.severity === "BLOCK")) {
      return;
    }

    const duplicateAddressIssue = nextIssues.find(
      (issue) => issue.kind === "duplicate_address" && issue.status === "open",
    );
    if (
      isDuplicateAddressReviewSegment(currentSegment) &&
      duplicateAddressIssue
    ) {
      setPendingAddressDuplicateWarning({
        hits: duplicateAddressIssue.hits,
        advanceAfterConfirm: true,
      });
      return;
    }

    dispatch({ type: "NEXT", maxStep: segments.length - 1 });
  }, [
    addressDuplicateConfirmationKey,
    currentStepRuleByUuid,
    currentSegment,
    evaluateUnsupportedCurrentStepRules,
    localStepValidation.hits,
    ruleContinueDisabled,
    segments.length,
    state.duplicateAddressResolution?.addressKey,
    state.registrationIssues,
  ]);

  const continuePastAddressDuplicateWarning = useCallback(() => {
    if (pendingAddressDuplicateWarning?.hits.length) {
      dispatch({
        type: "UPDATE",
        payload: {
          registrationIssues: upsertDuplicateAddressIssue(
            state.registrationIssues,
            {
              addressKey: addressDuplicateConfirmationKey,
              hits: pendingAddressDuplicateWarning.hits,
            },
          ),
        },
      });
    }
    const shouldAdvance = pendingAddressDuplicateWarning?.advanceAfterConfirm;
    setPendingAddressDuplicateWarning(null);
    if (shouldAdvance) {
      dispatch({ type: "NEXT", maxStep: segments.length - 1 });
    }
  }, [
    addressDuplicateConfirmationKey,
    pendingAddressDuplicateWarning,
    segments.length,
    state.registrationIssues,
  ]);

  // "Go back" from the duplicate modal: dismiss the warning and return to the
  // address/host step so the operator can edit the address before retrying.
  const goBackToAddressStepFromDuplicate = useCallback(() => {
    setPendingAddressDuplicateWarning(null);
    const hostStepIndex = segments.findIndex((segment) =>
      HOST_STEP_SEGMENTS.has(segment),
    );
    if (hostStepIndex >= 0) {
      dispatch({ type: "GO_TO", step: hostStepIndex });
    }
  }, [segments]);

  // Attach a continued duplicate registration to one of the matched property's
  // existing structures ("add a product to an existing structure"). The picker
  // for the NEW-structure path reads/writes the wizard host state, so point it
  // at the matched property host.
  const setDuplicateContinuationHost = useCallback(
    (hostUuid: string | null) => {
      dispatch({
        type: "UPDATE",
        payload: { selectedExistingHostUuid: hostUuid },
      });
    },
    [],
  );

  // WPS-38: when a registration continues against an existing property, the
  // Customer Details step should pre-fill with that property's current owner
  // rather than leaving the operator to retype known details. The pure mapping
  // (only-empty-fields, non-destructive) lives in duplicate-owner-prefill.ts.
  const prefillConsumerFromMatch = (
    match: DuplicateAddressMatchSummary | null | undefined,
  ): Partial<WizardState> =>
    consumerPrefillFromMatch(match, {
      consumerEmail: state.consumerEmail,
      consumerFirstName: state.consumerFirstName,
      consumerLastName: state.consumerLastName,
      consumerPhone: state.consumerPhone,
    });

  const resolveAddressDuplicateWarning = (
    decision: DuplicateAddressDecision,
    decisionLabel: string,
    notes: string,
    selectedHoldingUuid?: string,
    modalContext?: {
      matches: DuplicateAddressMatchSummary[];
      selectedMatch?: DuplicateAddressMatchSummary | null;
      structurePath?: "new_structure" | "existing_structure" | null;
      selectedHostUuid?: string | null;
      selectedStructureUuid?: string | null;
    },
  ) => {
    if (!pendingAddressDuplicateWarning?.hits.length) return;
    if (decision === "add_new_product") {
      const existing = state.registrationIssues.find(
        (issue) =>
          issue.kind === "duplicate_address" &&
          issue.addressKey === addressDuplicateConfirmationKey,
      );
      const shouldAdvance = pendingAddressDuplicateWarning.advanceAfterConfirm;
      setPendingAddressDuplicateWarning(null);
      const addNewProductMatch = modalContext?.selectedMatch ?? null;
      dispatch({
        type: "UPDATE",
        payload: {
          ...prefillConsumerFromMatch(addNewProductMatch),
          duplicateAddressResolution: {
            id:
              existing?.id ??
              `duplicate_address:${addressDuplicateConfirmationKey}`,
            decision,
            decisionLabel,
            notes,
            addressKey: addressDuplicateConfirmationKey,
            hits: pendingAddressDuplicateWarning.hits,
            resolutionContext: {
              propertyAddress: duplicateWarningAddress,
              ownerName: duplicateWarningCustomerName,
              existingOwnerName: duplicateWarningExistingOwner,
              installedDate: formatDateLong(state.purchaseDate),
              matches: modalContext?.matches ?? duplicateMatches,
              selectedMatch: addNewProductMatch,
              structurePath: modalContext?.structurePath ?? null,
              selectedHostUuid: modalContext?.selectedHostUuid ?? null,
              selectedStructureUuid:
                modalContext?.selectedStructureUuid ?? null,
            },
            resolvedAt: new Date().toISOString(),
          },
          registrationIssues: state.registrationIssues.filter(
            (issue) =>
              !(
                issue.kind === "duplicate_address" &&
                issue.addressKey === addressDuplicateConfirmationKey
              ),
          ),
        },
      });
      if (shouldAdvance) {
        dispatch({ type: "NEXT", maxStep: segments.length - 1 });
      }
      return;
    }
    if (decision === "confirmed_duplicate") {
      // No backend write - abandon the wizard and return to the registrations list.
      setPendingAddressDuplicateWarning(null);
      dispatch({ type: "RESET" });
      navigate("/registrations");
      return;
    }
    const existing = state.registrationIssues.find(
      (issue) =>
        issue.kind === "duplicate_address" &&
        issue.addressKey === addressDuplicateConfirmationKey,
    );
    const issueId =
      existing?.id ?? `duplicate_address:${addressDuplicateConfirmationKey}`;
    const shouldAdvance = pendingAddressDuplicateWarning.advanceAfterConfirm;
    const resolutionContext = {
      propertyAddress: duplicateWarningAddress,
      ownerName: duplicateWarningCustomerName,
      existingOwnerName: duplicateWarningExistingOwner,
      installedDate: formatDateLong(state.purchaseDate),
      matches: modalContext?.matches ?? duplicateMatches,
      selectedMatch:
        modalContext?.selectedMatch ??
        (selectedHoldingUuid
          ? (duplicateMatches.find(
              (match) => match.holding_uuid === selectedHoldingUuid,
            ) ?? null)
          : null),
    };
    dispatch({
      type: "UPDATE",
      payload: {
        // A merge continues against the matched property's owner, so pre-fill
        // the Customer step with that owner (WPS-38). Reject abandons the
        // match, so no prefill there.
        ...(decision === "merge"
          ? prefillConsumerFromMatch(resolutionContext.selectedMatch)
          : {}),
        duplicateAddressResolution:
          decision === "merge" || decision === "reject"
            ? {
                id: issueId,
                decision,
                decisionLabel,
                notes,
                addressKey: addressDuplicateConfirmationKey,
                hits: pendingAddressDuplicateWarning.hits,
                selectedHoldingUuid,
                resolutionContext,
                resolvedAt: new Date().toISOString(),
              }
            : null,
        registrationIssues: state.registrationIssues.filter(
          (issue) => issue.id !== issueId,
        ),
      },
    });
    setPendingAddressDuplicateWarning(null);
    if (shouldAdvance) {
      dispatch({ type: "NEXT", maxStep: segments.length - 1 });
    }
  };

  const doSubmit = useCallback(async (): Promise<SubmitRegistrationResult> => {
    const duplicateResolutionIssue =
      state.duplicateAddressResolution?.addressKey ===
      addressDuplicateConfirmationKey
        ? state.duplicateAddressResolution
        : null;
    const mergeReplacementMatch =
      duplicateResolutionIssue?.decision === "merge"
        ? duplicateResolutionIssue.resolutionContext?.selectedMatch
        : null;
    const addNewProductHostUuid =
      duplicateResolutionIssue?.decision === "add_new_product"
        ? (duplicateResolutionIssue.resolutionContext?.selectedHostUuid ??
          duplicateResolutionIssue.hits[0]?.duplicate_matches?.[0]?.host_uuid ??
          null)
        : null;
    // WP1-34 "add a product to an existing structure": attach the new holding
    // to the explicitly chosen structure rather than identity-matching from
    // host_data.
    const addNewProductStructureUuid =
      duplicateResolutionIssue?.decision === "add_new_product" &&
      duplicateResolutionIssue.resolutionContext?.structurePath ===
        "existing_structure"
        ? (duplicateResolutionIssue.resolutionContext?.selectedStructureUuid ??
          null)
        : null;
    // WPS-137: never send an existing-host link that was pinned to a different
    // property address than the one now on the form. The reactive reconcile
    // pass normally clears it already; this is the last-line guard.
    const submittableExistingHostUuid = resolveSubmittableExistingHostUuid(
      {
        selectedExistingHostUuid: state.selectedExistingHostUuid,
        selectedExistingHostAddressKey: state.selectedExistingHostAddressKey,
      },
      addressDuplicateConfirmationKey,
    );
    const hasConsumerSource = Boolean(
      state.consumerEmail ||
      mergeReplacementMatch?.consumer_account_uuid ||
      mergeReplacementMatch?.owner_email,
    );
    if (!state.selectedProduct || !hasConsumerSource) {
      toast.error("Please complete all required fields");
      return { type: "aborted" };
    }
    if (
      state.registrationIssues.some(
        (issue) =>
          issue.kind === "duplicate_address" && issue.status === "open",
      )
    ) {
      toast.error("Resolve the duplicate address warning before submitting.");
      return { type: "aborted" };
    }

    const attributeValues: Record<string, unknown> = unwrapStockAttributeValues(
      {
        ...state.earlyAttributes,
        ...state.dynamicAttributes,
      },
      metadata?.fields ?? [],
    );

    const hostPayload: Record<string, unknown> = {};

    if (metadata?.host_template) {
      // Generic host data: own host attributes, plus any RegistrationField
      // values that mirror onto a host attribute slot. Tenants drive
      // which fields exist via RegistrationField definitions and host
      // template config - no field name is hardcoded here.
      const hostData = mirrorRegistrationFieldsToHost(
        state.hostAttributeValues,
        state.registrationFieldValues,
        { dropEmpty: true },
      );
      if (Object.keys(hostData).length > 0) {
        hostPayload.host_data = hostData;
      }
      if (state.isInstalled !== null) {
        hostPayload.is_installed = state.isInstalled;
      }
    }

    for (const key of Object.keys(attributeValues)) {
      if (!attributeValues[key] && attributeValues[key] !== false) {
        delete attributeValues[key];
      }
    }

    const regFieldPayload: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(state.registrationFieldValues)) {
      if (val !== undefined && val !== null && val !== "") {
        regFieldPayload[key] = val;
      }
    }

    // Mailing address belongs to the customer/consumer entity. When it is
    // marked same-as-installation, derive it from the host address regardless
    // of whether the host template stores split address fields or one
    // object-backed ADDRESS value.
    const buildMailingPayload = ():
      import("@/shared/lib/formatting").AddressValue | undefined => {
      // A consumer address captured directly on the customer step always wins:
      // its field writes to `state.address` and is never gated by the
      // same-as-installation flag (that flag only exists on the host/property
      // mailing panel). Without this, a typed customer address is silently
      // dropped whenever `mailingSameAsInstallation` is left at its default.
      if (state.address) {
        const value = normaliseAddressValue(state.address);
        if (isAddressNonEmpty(value)) return value;
      }
      if (state.mailingSameAsInstallation) {
        const attrs = metadata?.host_template?.attributes ?? [];
        const byColumn: Record<string, string> = {};
        for (const a of attrs) {
          if (a.maps_to_column) byColumn[a.maps_to_column] = a.code_name;
        }
        const h = state.hostAttributeValues;
        for (const a of attrs) {
          const value = normaliseAddressValue(h[a.code_name]);
          if (isAddressNonEmpty(value)) return value;
        }
        const pick = (column: string): string => {
          const codeName = byColumn[column];
          if (!codeName) return "";
          const v = h[codeName];
          return typeof v === "string" ? v : "";
        };
        const value: import("@/shared/lib/formatting").AddressValue = {
          address_line_1: pick("address_line_1"),
          address_line_2: pick("address_line_2"),
          locality: pick("address_town_or_city"),
          region: pick("address_county"),
          postal_code: pick("address_postcode"),
          country_code: pick("address_country"),
        };
        return isAddressNonEmpty(value) ? value : undefined;
      }
      if (!state.address) return undefined;
      const value = normaliseAddressValue(state.address);
      return isAddressNonEmpty(value) ? value : undefined;
    };
    // Preferred, tenant-agnostic path: when the tenant has a consumer
    // ADDRESS-typed EAV attribute, store the captured customer address there
    // (keyed by its code_name) instead of the OC-only mailing_address
    // mechanism. Send the bare canonical AddressValue so the backend's
    // format_address reads it directly on the detail response.
    const consumerAddressField = metadata?.consumer_fields?.find(
      (f) => f.field_type === "ADDRESS",
    );
    const consumerAddressValue =
      consumerAddressField && state.address
        ? normaliseAddressValue(state.address)
        : null;
    const extraValues = Object.fromEntries(
      Object.entries(state.consumerExtraValues).filter(
        ([, v]) => v !== "" && v != null,
      ),
    );
    const mergedConsumerValues = {
      ...extraValues,
      ...(consumerAddressField &&
      consumerAddressValue &&
      isAddressNonEmpty(consumerAddressValue)
        ? { [consumerAddressField.code_name]: consumerAddressValue }
        : {}),
    };
    const consumerAttributeValues =
      Object.keys(mergedConsumerValues).length > 0
        ? mergedConsumerValues
        : null;
    // Fall back to the legacy mailing_address payload only for tenants without
    // a consumer ADDRESS attribute (i.e. OC's split mailing_* attributes).
    const mailingPayload = consumerAddressField
      ? undefined
      : buildMailingPayload();
    const duplicateAddressResolution =
      duplicateResolutionIssue?.decision &&
      (duplicateResolutionIssue.decision === "merge" ||
        duplicateResolutionIssue.decision === "reject")
        ? {
            decision: duplicateResolutionIssue.decision,
            issue_id: duplicateResolutionIssue.id,
            address_key: duplicateResolutionIssue.addressKey,
            reason_text: duplicateResolutionIssue.notes ?? "",
            resolution_context:
              duplicateResolutionIssue.resolutionContext ?? null,
            duplicate_holding_uuids:
              duplicateResolutionIssue.decision === "merge" &&
              duplicateResolutionIssue.selectedHoldingUuid
                ? [duplicateResolutionIssue.selectedHoldingUuid]
                : duplicateResolutionIssue.hits.flatMap((hit) =>
                    (hit.duplicate_matches ?? [])
                      .map((match) => match.holding_uuid)
                      .filter(Boolean),
                  ),
          }
        : undefined;

    const transactionPayload: Record<string, unknown> = {
      ...state.transactionData,
    };
    const transactionAttributeCodes = new Set(
      (metadata?.transaction_template?.attributes ?? []).map(
        (attribute) => attribute.code_name,
      ),
    );
    if (transactionAttributeCodes.has("purchase_date") && state.purchaseDate) {
      transactionPayload.purchase_date = state.purchaseDate;
    }
    // WPS-114: the warranty starts when the product was installed. Prefer the
    // captured installation date over the purchase/creation date when the
    // operator did not enter an explicit warranty start date. Tenants that do
    // not capture an installation date fall through to the previous behaviour.
    const effectiveWarrantyStartDate =
      state.warrantyStartDate || installationDate || state.purchaseDate;
    if (
      transactionAttributeCodes.has("warranty_start_date") &&
      effectiveWarrantyStartDate
    ) {
      transactionPayload.warranty_start_date = effectiveWarrantyStartDate;
    }
    if (
      transactionAttributeCodes.has("purchase_price") &&
      state.purchasePrice
    ) {
      transactionPayload.purchase_price = state.purchasePrice;
    }
    if (transactionAttributeCodes.has("sales_person") && state.salesPerson) {
      transactionPayload.sales_person = state.salesPerson;
    }
    if (
      transactionAttributeCodes.has("sales_reference") &&
      state.salesRefNumber
    ) {
      transactionPayload.sales_reference = state.salesRefNumber;
    }
    for (const key of Object.keys(transactionPayload)) {
      const value = transactionPayload[key];
      if (value === "" || value === null || value === undefined) {
        delete transactionPayload[key];
      }
    }

    try {
      const response = await createHolding.mutateAsync({
        product_uuid: state.selectedProduct.uuid,
        partnership_uuid: state.partnershipUuid || undefined,
        ...(mergeReplacementMatch?.consumer_account_uuid
          ? {
              consumer_account_uuid:
                mergeReplacementMatch.consumer_account_uuid,
            }
          : {
              consumer_email:
                state.consumerEmail || mergeReplacementMatch?.owner_email || "",
              ...(state.consumerEmailIsOwners === null
                ? {}
                : { consumer_email_is_owners: state.consumerEmailIsOwners }),
              consumer_first_name: state.consumerFirstName,
              consumer_last_name: state.consumerLastName,
              consumer_phone_number: state.consumerPhone,
            }),
        attribute_values: attributeValues,
        warranty_template_option_uuid:
          state.warrantyTemplateOptionUuid || undefined,
        active_from:
          state.warrantyStartDate ||
          installationDate ||
          state.purchaseDate ||
          undefined,
        purchase_price: state.purchasePrice || undefined,
        sales_person: state.salesPerson || undefined,
        sales_reference: state.salesRefNumber || undefined,
        ...(Object.keys(transactionPayload).length > 0
          ? { transaction_data: transactionPayload }
          : {}),
        ...(Object.keys(regFieldPayload).length > 0
          ? { registration_field_values: regFieldPayload }
          : {}),
        // Only ticked boxes: an unticked box never opts anyone out.
        consents: Object.fromEntries(
          Object.entries(state.consents).filter(([, ticked]) => ticked),
        ),
        // Send the canonical `{display, value}` envelope: the backend's
        // MailingAddressInputSerializer only reads the address from an
        // `address` key or auto-wraps a `{value, display}` envelope. A bare
        // AddressValue flattens to empty strings and fails the required check.
        ...(mailingPayload
          ? { mailing_address: buildAddress(mailingPayload) ?? undefined }
          : {}),
        ...(consumerAttributeValues
          ? { consumer_attribute_values: consumerAttributeValues }
          : {}),
        ...(duplicateAddressResolution
          ? { duplicate_address_resolution: duplicateAddressResolution }
          : {}),
        ...(addNewProductHostUuid || submittableExistingHostUuid
          ? {
              existing_host_uuid:
                addNewProductHostUuid || submittableExistingHostUuid,
            }
          : {}),
        ...(addNewProductStructureUuid
          ? { structure_uuid: addNewProductStructureUuid }
          : {}),
        ...hostPayload,
      });
      return { type: "submitted", response };
    } catch (err: unknown) {
      const response = (
        err as {
          response?: {
            status?: number;
            data?: {
              result?: { hits?: { severity: string; message: string }[] };
            };
          };
        }
      )?.response;
      if (response?.status === 422) {
        const hits = response.data?.result?.hits ?? [];
        if (hits.length > 0) {
          for (const hit of hits) {
            if (hit.severity === "BLOCK") toast.error(hit.message);
            else if (hit.severity === "WARN") toast.warning(hit.message);
            else toast.info(hit.message);
          }
        } else {
          toastApiError(err, "Validation failed");
        }
        return { type: "aborted" };
      }
      toastApiError(err, "Failed to create registration");
      throw err;
    }
  }, [
    state,
    metadata,
    createHolding,
    addressDuplicateConfirmationKey,
    installationDate,
  ]);

  const handleSubmit =
    useCallback(async (): Promise<SubmitRegistrationResult> => {
      const mergeReplacementMatch =
        state.duplicateAddressResolution?.decision === "merge" &&
        state.duplicateAddressResolution.addressKey ===
          addressDuplicateConfirmationKey
          ? state.duplicateAddressResolution.resolutionContext?.selectedMatch
          : null;
      const hasConsumerSource = Boolean(
        state.consumerEmail ||
        mergeReplacementMatch?.consumer_account_uuid ||
        mergeReplacementMatch?.owner_email,
      );
      if (!state.selectedProduct || !hasConsumerSource) {
        toast.error("Please complete all required fields");
        return { type: "aborted" };
      }

      return await doSubmit();
    }, [state, doSubmit, addressDuplicateConfirmationKey]);

  // Filter registration fields for the current segment.
  //
  // Disclaimer / terms fields (`display_as: 'disclaimer'`) are excluded here:
  // they are a catch-all surface rendered at the bottom of the Review step
  // (StepReview), never on their nominal capture step - so a consent field
  // is always the last thing acknowledged before submit, for any tenant.
  const stepRegFields = useMemo(() => {
    if (!registrationFieldsEnabled || !registrationFields) return [];
    return registrationFields.filter(
      (f) =>
        f.is_active &&
        f.capture_step === currentSegment &&
        !isDisclaimerRegistrationField(f),
    );
  }, [registrationFieldsEnabled, registrationFields, currentSegment]);

  // Fields with no capture_step go at the step before review. Disclaimer
  // fields are excluded (see above) - they surface on Review instead.
  const defaultRegFields = useMemo(() => {
    if (!registrationFieldsEnabled || !registrationFields) return [];
    // Show on the step before review (last non-review step)
    const lastContentIdx = segments.length - 2;
    if (currentStepIndex !== lastContentIdx) return [];
    return registrationFields.filter(
      (f) =>
        f.is_active && !f.capture_step && !isDisclaimerRegistrationField(f),
    );
  }, [
    registrationFieldsEnabled,
    registrationFields,
    segments,
    currentStepIndex,
  ]);

  const allRegFieldsForStep = useMemo(
    () => [...stepRegFields, ...defaultRegFields],
    [stepRegFields, defaultRegFields],
  );

  const combinedFormValues = useMemo<Record<string, unknown>>(
    () => ({
      ...state.hostAttributeValues,
      ...state.registrationFieldValues,
      // Expose the selected warranty template UUID so registration fields
      // with visible_when conditions keyed on warrantyTemplateUuid (e.g.
      // Solar PROtect fields) can evaluate correctly.
      warrantyTemplateUuid: state.warrantyTemplateUuid,
    }),
    [
      state.hostAttributeValues,
      state.registrationFieldValues,
      state.warrantyTemplateUuid,
    ],
  );

  // When the structure picker (on the host_context step) is the single
  // structure decision, it owns fields like `structure_type` /
  // `other_structure_type`. Those are registration fields validated here
  // against `registrationFieldValues`, but the picker writes the chosen
  // structure into `hostAttributeValues` - so requiring them here would
  // throw a false "required" error and block Continue. The picker reports
  // control + the code_names it owns; we drop those from this step's
  // effective fields (which feeds both validation and inline rendering).
  const [structurePickerActivation, setStructurePickerActivation] = useState<{
    active: boolean;
    ownedCodeNames: string[];
  }>({ active: false, ownedCodeNames: [] });
  const handleStructurePickerActivation = useCallback(
    (activation: { active: boolean; ownedCodeNames: string[] }) => {
      setStructurePickerActivation((prev) =>
        prev.active === activation.active &&
        prev.ownedCodeNames.join(",") === activation.ownedCodeNames.join(",")
          ? prev
          : activation,
      );
    },
    [],
  );
  const structurePickerOwnedCodes = useMemo(
    () =>
      structurePickerActivation.active &&
      (currentSegment === "host_context" ||
        currentSegment === "host_product" ||
        currentSegment === "host")
        ? new Set(structurePickerActivation.ownedCodeNames)
        : new Set<string>(),
    [structurePickerActivation, currentSegment],
  );

  const effectiveRegFieldsForStep = useMemo<
    EffectiveRegistrationField[]
  >(() => {
    return allRegFieldsForStep.flatMap((field) => {
      // Owned by the structure picker - it is the structure decision and
      // carries these values via hostAttributeValues.
      if (structurePickerOwnedCodes.has(field.code_name)) return [];
      const cd = field.configuration_data as
        Record<string, unknown> | undefined;
      if (cd?.visible_when && !isFieldVisible(cd, combinedFormValues)) {
        return [];
      }
      const required =
        (cd && isConditionallyRequired(cd, combinedFormValues)) ||
        field.required;
      return [{ field, required, requiredMessage: null }];
    });
  }, [allRegFieldsForStep, combinedFormValues, structurePickerOwnedCodes]);

  const registrationFieldsContinueDisabled = useMemo(
    () =>
      effectiveRegFieldsForStep.some(
        ({ field, required }) =>
          required &&
          !isRegistrationFieldValueProvided(
            field,
            state.registrationFieldValues[field.code_name],
          ),
      ),
    [effectiveRegFieldsForStep, state.registrationFieldValues],
  );

  const stepConfigIssues = useMemo(
    () =>
      detectStepConfigIssues({
        segment: currentSegment,
        segments,
        hostTemplateAttributes: metadata?.host_template?.attributes ?? null,
        hostStepSegments: HOST_STEP_SEGMENTS,
        hostInstalled: state.isInstalled,
        hostRequirement: metadata?.host_requirement ?? "NONE",
        registrationFields: registrationFields ?? null,
        registrationFieldsEnabled,
      }),
    [
      currentSegment,
      segments,
      metadata?.host_template?.attributes,
      metadata?.host_requirement,
      state.isInstalled,
      registrationFields,
      registrationFieldsEnabled,
    ],
  );

  const stepConfigBlocking = stepConfigIssues.some((issue) => issue.required);

  // Page-level hard blockers: things the user cannot fix by editing
  // an inline field (rule BLOCK hits, server validation in flight,
  // misconfigured step). These are surfaced via dedicated alerts above
  // and registered as a wizard validator so clicking Continue won't
  // advance the step.
  const pageHardBlock = ruleContinueDisabled || stepConfigBlocking;

  // Backward-compat hints / disabled values still passed to step
  // components for any custom UI they render. The Continue button
  // itself ignores `continueDisabled`; required-field checks happen
  // inline via the wizard validation context.
  const stepContinueDisabled =
    pageHardBlock || registrationFieldsContinueDisabled;

  // Required registration-field messages render inline beneath each field
  // (via RegistrationFieldsPanel), so the footer hint only carries
  // step-config / rule-level blocks - not per-field "is required" text.
  const stepContinueHint = stepConfigBlocking
    ? "This step is misconfigured - see the inline error."
    : ruleContinueHint;

  const reviewSubmissionWarningHits = useMemo(
    () =>
      currentSegment === "review"
        ? visibleStepRuleHits.filter((hit) => hit.severity !== "BLOCK")
        : [],
    [currentSegment, visibleStepRuleHits],
  );

  const reviewSubmissionBlockedMessage =
    currentSegment === "review" && currentStepBlockingHits.length > 0
      ? ruleContinueHint
      : null;

  // Pre-evaluate ApprovalRule predicates locally on the review step so the
  // wizard can surface an "approval will be required" confirmation modal
  // before submitting. Only BLOCK hits gate activation on the backend (via
  // ``submit_registration`` raising a generic ``WorkflowIssue`` on the
  // lifecycle workflow), so that is what we preview here. Predicates that
  // the local evaluator can't support silently fall through - the backend
  // still raises the workflow issue if it matches server-side.
  const reviewSubmissionApprovalHits = useMemo<ApprovalRuleHit[]>(() => {
    if (currentSegment !== "review" || !rulesEnabled) return [];
    const hits: ApprovalRuleHit[] = [];
    for (const rule of approvalRules as ApprovalRule[]) {
      if (!rule.is_active || rule.severity !== "BLOCK" || !rule.conditions) {
        continue;
      }
      const result = evaluatePredicateLocally(rule.conditions, ruleContext);
      if (!result.supported || !result.matched) continue;
      hits.push({
        rule_uuid: rule.uuid,
        name: rule.name,
        severity: rule.severity,
        message: rule.message || rule.name,
        target_status: rule.target_status,
      });
    }
    return hits;
  }, [approvalRules, currentSegment, ruleContext, rulesEnabled]);

  // Split the active step's effective registration fields into two
  // buckets: fields whose visibility rule references a host attribute
  // ("anchored") vs. all others ("unanchored"). Anchored fields render
  // inline immediately under their trigger host attribute (e.g. the
  // `other_structure_type` dropdown sits right below `structure_type`)
  // so the dependency reads naturally; unanchored fields keep their
  // existing slot below the host inputs.
  //
  // Anchoring is only meaningful on `host_context` - other steps don't
  // render host attributes inline, so every field stays in the panel.
  const { anchoredByHostAttr, unanchoredFields } = useMemo(() => {
    const anchoredByHostAttr = new Map<string, EffectiveRegistrationField[]>();
    const unanchoredFields: EffectiveRegistrationField[] = [];

    if (
      currentSegment !== "host_context" ||
      effectiveRegFieldsForStep.length === 0
    ) {
      return {
        anchoredByHostAttr,
        unanchoredFields: effectiveRegFieldsForStep,
      };
    }

    for (const eff of effectiveRegFieldsForStep) {
      const vw = (eff.field.configuration_data as Record<string, unknown>)
        ?.visible_when as Record<string, unknown> | undefined;
      const anchor =
        vw && typeof vw === "object" && !Array.isArray(vw)
          ? (Object.keys(vw)[0] ?? null)
          : null;

      if (anchor) {
        const bucket = anchoredByHostAttr.get(anchor) ?? [];
        bucket.push(eff);
        anchoredByHostAttr.set(anchor, bucket);
      } else {
        unanchoredFields.push(eff);
      }
    }

    return { anchoredByHostAttr, unanchoredFields };
  }, [currentSegment, effectiveRegFieldsForStep]);

  const handleRegistrationFieldChange = useCallback(
    (vals: Record<string, unknown>) => {
      update({
        registrationFieldValues: {
          ...state.registrationFieldValues,
          ...vals,
        },
      });
    },
    [state.registrationFieldValues, update],
  );

  // The shared RegistrationFieldsPanel rendered for the active step. For most
  // steps it is slotted into the step's RegistrationStepGrid (via `extraPanels`)
  // so it sits to the right of the step's own panels in a 2x2 grid. For the
  // standalone `registration_details` step it is rendered separately above the
  // step actions (handled in JSX below).
  const registrationFieldsPanelNode = useMemo(() => {
    if (unanchoredFields.length === 0) return null;
    // On the host_context step the fields render inside the Property
    // panel (passed through `propertyExtras`), so they should appear as
    // bare inputs rather than a separate bordered panel.
    const variant = currentSegment === "host_context" ? "inline" : "panel";
    // On the product step these registration fields are the roofing-component
    // capture (the per-component Yes/No toggles that replaced the legacy
    // installed_components multi-select), so title the panel accordingly.
    const title =
      currentSegment === "product" ? "Installed Components" : undefined;
    return (
      <RegistrationFieldsPanel
        variant={variant}
        title={title}
        fields={unanchoredFields.map(({ field, required }) => ({
          ...field,
          required,
        }))}
        values={state.registrationFieldValues}
        onChange={handleRegistrationFieldChange}
      />
    );
  }, [
    unanchoredFields,
    state.registrationFieldValues,
    handleRegistrationFieldChange,
    currentSegment,
  ]);

  const handleGroupedHoldingChange = useCallback(
    (vals: Record<string, unknown>) => {
      update({
        dynamicAttributes: {
          ...state.dynamicAttributes,
          ...vals,
        },
      });
    },
    [state.dynamicAttributes, update],
  );

  // Combined values used to resolve visible_when / conditional-required on the
  // grouped holding attributes. Mirrors `combinedFormValues` for reg fields:
  // the captured holding values plus the selected warranty template so a
  // dependent picker (e.g. `<code>_product` gated on `used_<code>`) evaluates
  // against what the user has entered this step.
  const combinedHoldingValues = useMemo<Record<string, unknown>>(
    () => ({
      ...state.dynamicAttributes,
      warrantyTemplateUuid: state.warrantyTemplateUuid,
    }),
    [state.dynamicAttributes, state.warrantyTemplateUuid],
  );

  // Holding EAV attributes tagged with `configuration_data.registration_group`
  // render in their own bordered panel (via extraPanels), grouped by that name,
  // instead of the generic Additional Details form (which excludes them - see
  // shared.tsx). This is the config-driven, tenant-agnostic home for captures
  // like OC's "Installed Components" now that they live as product-template EAV
  // rather than tenant-wide registration fields. The render path is the shared
  // RegistrationFieldsPanel, so the fields look byte-for-byte identical to when
  // they were registration fields; only the storage/source changed.
  const groupedHoldingPanelsNode = useMemo(() => {
    const fields = metadata?.fields;
    if (!fields) return null;
    // Holding attributes are only captured on the product / host+product steps.
    if (currentSegment !== "product" && currentSegment !== "host_product") {
      return null;
    }
    type MetaField = (typeof fields)[number];
    const groups = new Map<string, MetaField[]>();
    for (const f of fields) {
      if (f.attribute_level !== "HOLDING") continue;
      const group = f.configuration_data?.registration_group;
      if (typeof group !== "string" || !group) continue;
      const bucket = groups.get(group) ?? [];
      bucket.push(f);
      groups.set(group, bucket);
    }
    if (groups.size === 0) return null;

    const panels: React.ReactNode[] = [];
    for (const [groupName, groupFields] of groups) {
      // Order so a toggle trigger precedes its conditional picker (the panel
      // folds a visible_when field beneath the preceding non-conditional one).
      const ordered = [...groupFields].sort(
        (a, b) => (a.order ?? 0) - (b.order ?? 0),
      );
      const effective = ordered.flatMap((field) => {
        const cd = field.configuration_data as
          | Record<string, unknown>
          | undefined;
        if (cd?.visible_when && !isFieldVisible(cd, combinedHoldingValues)) {
          return [];
        }
        const required =
          (cd && isConditionallyRequired(cd, combinedHoldingValues)) ||
          field.required;
        return [{ ...field, required }];
      });
      if (effective.length === 0) continue;
      panels.push(
        <RegistrationFieldsPanel
          key={groupName}
          variant="panel"
          title={groupName}
          fields={effective}
          values={state.dynamicAttributes}
          onChange={handleGroupedHoldingChange}
        />,
      );
    }
    if (panels.length === 0) return null;
    return <>{panels}</>;
  }, [
    metadata?.fields,
    currentSegment,
    combinedHoldingValues,
    state.dynamicAttributes,
    handleGroupedHoldingChange,
  ]);

  // Map of host_attribute code_name → rendered RegistrationField nodes
  // for fields anchored to that attribute via a field-visibility rule.
  // `StepHostContext` slots each entry into the address panel directly
  // below the matching host attribute input.
  const propertyAnchoredRegistrationFields = useMemo<
    Record<string, React.ReactNode> | undefined
  >(() => {
    if (currentSegment !== "host_context" || anchoredByHostAttr.size === 0) {
      return undefined;
    }
    const result: Record<string, React.ReactNode> = {};
    for (const [hostCode, fields] of anchoredByHostAttr) {
      result[hostCode] = (
        <RegistrationFieldsPanel
          variant="inline"
          fields={fields.map(({ field, required }) => ({
            ...field,
            required,
          }))}
          values={state.registrationFieldValues}
          onChange={handleRegistrationFieldChange}
        />
      );
    }
    return result;
  }, [
    currentSegment,
    anchoredByHostAttr,
    state.registrationFieldValues,
    handleRegistrationFieldChange,
  ]);

  // Render the step for the current segment
  const stepContent = useMemo(() => {
    if (!currentSegment) return null;

    // Pass the registration-fields panel as an `extraPanels` slot for steps
    // that participate in the grid layout. The dedicated `registration_details`
    // step renders the panel itself (full-width form), and the `review` step
    // intentionally suppresses inline registration fields, so for those two
    // we don't pass the panel through the slot.
    const slotExtraPanels =
      currentSegment !== "registration_details" && currentSegment !== "review"
        ? (
            <>
              {registrationFieldsPanelNode}
              {groupedHoldingPanelsNode}
            </>
          )
        : null;
    return renderSegment(
      currentSegment,
      // Pass `back` whenever there is a previous step. Each step renders
      // it inline in `RegistrationStepActions`, paired with Continue.
      {
        state,
        update,
        next,
        back: currentStepIndex > 0 ? back : undefined,
        onStructurePickerActivation: handleStructurePickerActivation,
      },
      {
        metadata,
        selectableTemplates,
        templates: templates ?? [],
        geoEnabled,
        warrantyTemplates,
        warrantyContextExcludedFieldCodes,
        handleSubmit,
        isSubmitting: createHolding.isPending,
        continueDisabled: stepContinueDisabled,
        continueHint: stepContinueHint,
        submissionWarningHits: reviewSubmissionWarningHits,
        submissionBlockedMessage: reviewSubmissionBlockedMessage,
        submissionApprovalHits: reviewSubmissionApprovalHits,
        enableExistingHostSelection,
        flatProductSelection,
        productTemplateName: effectiveProductTemplateName,
        hostSelectionDismissed,
        setHostSelectionDismissed,
        onReset: () => dispatch({ type: "RESET" }),
        dataCaptureConfig: regSettings?.data_capture_config,
        earlyCaptureFields: regSettings?.early_capture_fields ?? [],
        // The installer is captured on the Property & Details step when the
        // registration template lists it as an early attribute; suppress the
        // duplicate contractor picker on the Customer step in that case.
        installerCapturedEarly: !!resolveRegistrationTemplate(
          templates ?? [],
          state.productTemplateUuid,
        )?.early_registration_attributes?.includes("installer_name"),
        ruleContext,
        extraPanels: slotExtraPanels,
        propertyAnchoredRegistrationFields,
        segments,
        returnTo,
        claimSchemaKey: locationClaimSchemaKey,
      },
    );
  }, [
    currentSegment,
    currentStepIndex,
    state,
    update,
    next,
    back,
    handleStructurePickerActivation,
    metadata,
    selectableTemplates,
    templates,
    geoEnabled,
    warrantyTemplates,
    warrantyContextExcludedFieldCodes,
    handleSubmit,
    createHolding.isPending,
    stepContinueDisabled,
    stepContinueHint,
    reviewSubmissionWarningHits,
    reviewSubmissionBlockedMessage,
    reviewSubmissionApprovalHits,
    enableExistingHostSelection,
    flatProductSelection,
    effectiveProductTemplateName,
    hostSelectionDismissed,
    regSettings?.data_capture_config,
    regSettings?.early_capture_fields,
    ruleContext,
    registrationFieldsPanelNode,
    groupedHoldingPanelsNode,
    propertyAnchoredRegistrationFields,
    segments,
    returnTo,
    locationClaimSchemaKey,
  ]);

  const duplicateWarningAddress =
    formatPropertyLineForDuplicateModal(state, metadata, registrationFields) ||
    addressDisplay(ruleContext.entities?.host) ||
    addressDisplay(state.address) ||
    "Address unavailable";
  const duplicateWarningCustomerName =
    [state.consumerFirstName, state.consumerLastName]
      .filter(Boolean)
      .join(" ") || "Customer";
  const duplicateMatches =
    pendingAddressDuplicateWarning?.hits.flatMap(
      (hit) => hit.duplicate_matches ?? [],
    ) ?? [];
  const duplicateWarningExistingOwner =
    duplicateMatches.find((match) => match.owner_name)?.owner_name ??
    duplicateMatches.find((match) => match.owner_email)?.owner_email ??
    "-";
  const duplicateWarningIssue: RegistrationWizardIssue | null =
    pendingAddressDuplicateWarning
      ? {
          id: `duplicate_address:${addressDuplicateConfirmationKey}`,
          kind: "duplicate_address",
          severity: pendingAddressDuplicateWarning.hits[0]?.severity ?? "WARN",
          message:
            pendingAddressDuplicateWarning.hits[0]?.message ??
            "A registration already exists with this address.",
          addressKey: addressDuplicateConfirmationKey,
          hits: pendingAddressDuplicateWarning.hits,
          status: "open",
        }
      : null;

  return (
    <ResourceLookupContextProvider providers={resourceLookupProviders}>
      <AppShell>
        <div className="flex min-h-[calc(100vh-48px)] flex-col bg-[var(--bg)]">
          {/* Header */}
          <div className="border-b-[0.5px] border-b-[var(--rule)]">
            <div className="page-container py-5 pb-4">
              <div className="mb-4 flex items-center justify-between">
                <h1 className="m-0 text-[32px] leading-[1.1] font-display text-[var(--ink)]">
                  New Registration
                </h1>
                <div className="flex items-center gap-3">
                  <ConfigHelp
                    sections={configSectionsForSegment(currentSegment)}
                    returnLabel={REGISTRATION_RETURN_LABEL}
                  />
                  {!state.registrationComplete && (
                    <Button
                      onClick={() => {
                        // Discard the in-progress draft so cancelling starts
                        // fresh next time instead of silently resuming.
                        clearWizardCache(cacheKey);
                        navigate("/registrations");
                      }}
                      variant="link"
                      className="text-[11px] font-light uppercase tracking-[0.1em] cursor-pointer transition-all duration-150 p-0 h-auto text-[var(--ink-3)] hover:text-[var(--ink)]"
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </div>

              {/* Clara Vale inline stepper - compact, left-anchored.
                Rendered as a neutral skeleton while real registration
                settings are still loading so we don't briefly show the
                wrong segment_order. */}
              <div
                aria-busy={wizardLoading || undefined}
                className="mb-0 flex max-w-[600px] items-center"
              >
                {wizardLoading
                  ? Array.from({ length: 4 }).map((_, i) => (
                      <div
                        key={`stepper-skeleton-${i}`}
                        className="flex items-center"
                      >
                        <div className="flex items-center gap-1.5">
                          <span className="h-[22px] w-[22px] shrink-0 rounded-full bg-[var(--skeleton-base)]" />
                          <span className="hidden h-[10px] w-[64px] rounded-[2px] bg-[var(--skeleton-base)] sm:inline-block" />
                        </div>
                        {i < 3 && (
                          <div className="mx-1 h-px w-5 shrink-0 bg-[var(--rule)] sm:mx-2 sm:w-10" />
                        )}
                      </div>
                    ))
                  : stepLabels.map((label, i) => (
                      <div
                        key={`${segments[i]}-${i}`}
                        className="flex items-center"
                      >
                        <button
                          onClick={() => {
                            if (i < currentStepIndex)
                              dispatch({ type: "GO_TO", step: i });
                          }}
                          className={`flex items-center gap-1.5 transition-all duration-150 ${
                            i < currentStepIndex
                              ? "cursor-pointer"
                              : "cursor-default"
                          }`}
                        >
                          <span
                            className={`flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full text-[9px] font-light ${
                              i <= currentStepIndex ||
                              state.registrationComplete
                                ? "bg-[var(--green)] text-white"
                                : "border-[0.5px] border-[var(--rule-strong)] bg-transparent text-[var(--ink-3)]"
                            }`}
                          >
                            {i < currentStepIndex || state.registrationComplete
                              ? "\u2713"
                              : i + 1}
                          </span>
                          <span
                            className={`hidden whitespace-nowrap text-[10px] uppercase tracking-[0.12em] sm:inline ${
                              i <= currentStepIndex
                                ? "text-[var(--ink)]"
                                : "text-[var(--ink-3)]"
                            }`}
                          >
                            {label}
                          </span>
                        </button>
                        {i < stepLabels.length - 1 && (
                          <div className="mx-1 h-px w-5 shrink-0 bg-[var(--rule-strong)] sm:mx-2 sm:w-10" />
                        )}
                      </div>
                    ))}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1">
            <div className="page-container py-5">
              {wizardLoading ? (
                <WizardStepSkeleton />
              ) : (
                <WizardValidationProvider key={currentSegment ?? "unknown"}>
                  <PageHardBlockValidator blocked={pageHardBlock} />
                  <StepConfigurationAlert issues={stepConfigIssues} />
                  {visibleStepRuleHits.length > 0 && (
                    <RegistrationRuleAlerts hits={visibleStepRuleHits} />
                  )}
                  {(() => {
                    const section =
                      primaryConfigSectionForSegment(currentSegment);
                    return section ? (
                      <div className="mb-3 flex justify-end">
                        <EditConfigLink
                          section={section}
                          returnLabel={REGISTRATION_RETURN_LABEL}
                        />
                      </div>
                    ) : null;
                  })()}
                  {currentSegment === "registration_details" ? (
                    <div className="max-w-[640px]">
                      {registrationFieldsPanelNode}
                      {stepContent}
                    </div>
                  ) : (
                    stepContent
                  )}
                </WizardValidationProvider>
              )}
            </div>
          </div>
        </div>

        {exceptionsEnabled && pendingAddressDuplicateWarning ? (
          <DuplicateAddressDecisionModal
            open={!!pendingAddressDuplicateWarning}
            issue={duplicateWarningIssue}
            propertyAddress={duplicateWarningAddress}
            duplicateMatches={duplicateMatches}
            hostLookupData={state.hostAttributeValues}
            state={state}
            update={update}
            onClose={() => setPendingAddressDuplicateWarning(null)}
            onGoBack={goBackToAddressStepFromDuplicate}
            onPrepareHost={setDuplicateContinuationHost}
            onResolve={resolveAddressDuplicateWarning}
          />
        ) : (
          <ConfirmationDialog
            open={!!pendingAddressDuplicateWarning}
            onOpenChange={(open) => {
              if (!open) {
                setPendingAddressDuplicateWarning(null);
              }
            }}
            title="Possible duplicate address"
            description={
              <div className="space-y-2">
                <p className="m-0">
                  {pendingAddressDuplicateWarning?.hits[0]?.message ??
                    "A registration already exists with this address."}
                </p>
                <p className="m-0">
                  You can continue with this address, or edit it before moving
                  on.
                </p>
              </div>
            }
            cancelLabel="Edit address"
            confirmLabel="Continue anyway"
            onConfirm={continuePastAddressDuplicateWarning}
          />
        )}
      </AppShell>
    </ResourceLookupContextProvider>
  );
}
